// The app's data: the five plan entities, and where each one lives.
//
//   odds-event    today's snapshot from /api/odds (lib/odds.js), kept on the
//                 device for the day. Never stored row by row — it is the
//                 bookmaker's data, not yours, and tomorrow it is useless.
//   multi, leg    DERIVED from the snapshot by lib/tickets.js, so the same day
//                 always prints the same four tickets. A multi and its legs are
//                 written to IndexedDB only when you mark it placed, frozen as
//                 they were, because by tomorrow the snapshot that made them
//                 is gone.
//   risk-profile  one row, in prefs.
//   bet-record    IndexedDB, one per placed multi.
//
// This file is the app's own now (it replaced the generated store), so
// `apply_plan` leaves it alone unless forced.
//
// /demo runs on a sample day and a sample history and writes nothing.

import { ref, computed, watch } from 'vue';
import { demoData, isDemo } from './core/demo.js';
import { records, prefs } from './core/storage.js';
import { TIER_ORDER, handTicket, normaliseOdds, localDate } from './lib/tickets.js';
// A leaf too: when a deal is cut, as opposed to when its prices were read.
import { dealAsOf, isoSeconds } from './lib/deal-cutoff.js';
// The day's proposals as one running list: the ordering, the fold at "now" and
// the states. Pure, and it never fetches anything — see the file.
import {
  emptySlots, legSetOf, slotOf, ticketKey, ticketState, timelineOf, variesFrom,
} from './lib/timeline.js';
// Pure copy, and a leaf on `format.js`: a slot key in, the name a punter reads
// out. The sentences a deal is reported in live beside it — see the file.
import { slotName } from './lib/deal-copy.js';
// A leaf built on `lib/tickets.js` and nothing else: when a result has stopped
// being on its way and started being missing.
import { legsOverdue } from './lib/results-due.js';
import { BOOKMAKERS, bookmakerName, cachedSnapshot, fetchSnapshot, qaSeed, getOddsCredits } from './lib/odds.js';
import {
  demoSnapshot, demoHistoryRows, demoRaceCard, demoRaceFeatures, demoRaceMovers,
} from './lib/demo.js';
// Racing's own ingest. A race goes in and canonical rows come out, in the same
// shape `fromOddsApiEvents` answers, so `buildUniverse` folds a card of races
// exactly as it folds a board of matches. See lib/racing/board.js.
import { fromRaces, RACING_TZ } from './lib/racing/board.js';
import {
  cachedRacingCard, storedRacingCard, clearRacingCard, fetchRacingCard, fetchRacingFeatures, fetchRacingMovers,
  RACING_PROVIDER,
} from './lib/racing/fetch.js';
// The Betfair Hub's free ratings, joined onto the card by venue and saddlecloth.
// A SECOND OPINION: it puts `rating` on a runner and changes no price. See
// lib/racing/ratings.js.
import { attachRatings } from './lib/racing/ratings.js';
// Which minimum-bet schedule a race falls under: the code, and metro or country.
// A leaf (it imports nothing) and the same table the exchange-trust gate reads.
import { codeFor, venueOfKey } from './lib/racing/trust.js';
// Pure, and a leaf on the racing domain: which of the three codes this device
// deals from. The record lives here; every rule about it is in that file.
import {
  codesParam, codesToSports, enabledCodes, normaliseCodes, racesForCodes, setCode,
  RACING_CODES_PREF,
} from './lib/racing/codes.js';
// Pure, and a leaf on the racing domain: WHEN the card may be bought without
// anybody asking — the hours, the staleness and the daily cap. This file keeps
// the tally and does the spending; every rule about it is in that one.
import {
  autoPullsOn, nextAutoPulls, shouldAutoPull, withinRacingHours, RACING_AUTO_PREF,
} from './lib/racing/auto.js';
// Pure, and a leaf on the racing domain: mover rows go in and the races they
// belong to come out, filed by runner. See lib/racing/view.js.
import { moversByRace, racingOutliers } from './lib/racing/view.js';
import { fromOddsApiEvents, buildUniverse } from './lib/engine/universe.js';
// Pure, and a leaf on the engine: a board goes in and the prices that are out
// of line with the rest of the market come out. It is what decides whether a
// board that just landed is worth a notification.
import { findOutliers } from './lib/engine/outliers.js';
import { runStrategy } from './lib/engine/strategy.js';
// The day's exposure budget, made once a pass and shared by every strategy in
// it (engine review #13).
import { createBudget } from './lib/engine/kelly.js';
// Pure, and a leaf on the engine: a ticket goes in and the same ticket comes
// back a leg shorter or a leg longer, every figure on it re-done. It re-shops
// nothing and fetches nothing — see the file.
import {
  buildTicket, legForTicket, newTicket, rankCandidates, withLegs, withoutLeg,
} from './lib/engine/reprice.js';
import { CATALOGUE } from './lib/engine/strategies/catalogue.js';
// A leaf: the ids retired strategies have been merged into. See the file.
import { canonicalStrategyId } from './lib/engine/strategies/aliases.js';
import { CLASSIC } from './lib/engine/strategies/classic.js';
// WHICH BOARD A STRATEGY DEALS FROM, read off the document rather than off a
// list of ids, so a fork of a racing strategy deals from the card too.
import { isRacingStrategy, NO_RACING_CARD } from './lib/engine/strategies/racing.js';
// Every strategy runs, so Today needs what you saved as well as the catalogue.
// From the LEAF module, not `stores/strategies.js` — that one imports this file
// and a cycle between them is a module-order bug waiting to happen.
import { myStrategies, toDocument, byName } from './lib/stores/strategy-state.js';
// The ranking, and the sections it orders. Both are leaves: `engine/picks.js`
// reads the board and nothing else, `sections.js` imports nothing at all.
import { rankTickets, topPicks } from './lib/engine/picks.js';
import { sectionsFor } from './lib/sections.js';
// Both leaf modules: they read storage and the engine and never import this
// file, so the pad can hold a model state and a probability set without a
// cycle. Same rule as `strategy-state.js` above.
import {
  bindPadModels, ensureModels, modelFits, modelsNeededFor, provenFits,
} from './lib/stores/models.js';
import { seedProbSets, selectedProbSet } from './lib/stores/probsets.js';
// A ref, not the stored value: the pad re-runs when Settings changes the book.
import { marketBook, getProviderKey, providerCredits } from './lib/providers.js';
import { migrateRows } from './lib/migrate.js';
import { sameDeal, matchIndex, stampLegs } from './lib/sets.js';
// Leaf module: it reads prefs and nothing else, so keeping the pad and pruning
// what is kept cannot pull the store into a cycle.
import {
  forgetProposal, pruneOld, recordPicks, recordRejections,
  GENERATIONS_KEY, PICKS_KEY, REJECTIONS_KEY, KEEP_DAYS,
} from './lib/proposals.js';
// The other half of the record: the legs the gate refused, kept with the price
// they would have taken so the bar can be scored against the close like
// anything else. Pure, and a leaf.
import { rejectionsFrom } from './lib/rejections.js';
// Pure, and a leaf: the stake limits and the break, with no clock and no
// storage of their own. Every rule about them is in that file; what is here is
// the profile they read and the two places they are enforced.
import {
  applyLimitChange, breakLine, liveLimits, onBreak as onBreakAt,
  bookCap, stakeAllowed, stakedOn, startBreak, SYDNEY,
} from './lib/limits.js';

const cents = (n) => Math.round(n * 100) / 100;
const newestFirst = (a, b) => String(b.placedAt).localeCompare(String(a.placedAt));
const inBrowser = typeof window !== 'undefined';

// `today` is the device's own day, so the universe has to be cut on the same
// zone or a late match would be on one side of midnight and not the other.
const deviceZone = () => {
  try { return globalThis.Intl.DateTimeFormat().resolvedOptions().timeZone || undefined; } catch { return undefined; }
};

// --- the clock ---------------------------------------------------------------
// A ticket goes STALE and a leg goes "started" with time, not with data, so
// the screens need a clock that ticks. Once a minute is enough for both.
export const now = ref(Date.now());
export const today = computed(() => localDate(now.value));
if (inBrowser) setInterval(() => { now.value = Date.now(); }, 60_000);

/**
 * The moment the pad is DEALING as of. A match that has started by here is not
 * on a new set, unless the profile says it may be.
 *
 * A REF, WRITTEN AT THREE EVENTS, AND NEVER A COMPUTED OVER THE CLOCK. The
 * board is a computed and `now` ticks every minute, so a cutoff that followed
 * the clock would take tickets off the live pad as matches kicked off, one
 * every few minutes, with nobody having touched anything. That is not what
 * anyone asked for: a printed ticket whose match has since started is the
 * honest state of a proposal, and `Ticket.vue` already stamps "started" beside
 * the leg. It moves only when somebody acts:
 *
 *   a  a board with a different `fetchedAt` lands (the watcher below) — not on
 *      a `loadToday()` cache hit, so reopening the app does not reshuffle;
 *   b  a deal — `dealMore()`, or a slot refilling itself as a ticket expires —
 *      once the pad it is replacing has been captured;
 *   c  "Only matches yet to start" is switched ON (`setAllowStarted`).
 *
 * Stamped from `now.value` rather than `Date.now()`: that is the same clock
 * `Ticket.vue` prints "started" from, so the pad can never deal a leg that the
 * badge beside it would call started on the same tick. The clock is refreshed
 * on `visibilitychange`, so a cold open is exact and the worst drift is the
 * minute between ticks.
 */
export const dealCutoff = ref(Date.now());

// --- risk-profile ------------------------------------------------------------
const PROFILE_DEFAULTS = {
  id: 'me',
  defaultStake: 10,
  bookmaker: 'sportsbet',
  // The two notification settings, both OFF as shipped. An app that starts
  // sending notifications before anybody asked it to is an app whose
  // notifications get switched off at the OS, where Settings cannot switch them
  // back on. `notifyAt` is the reminder's time and means nothing until
  // `notifyDaily` is on.
  notifyAt: '09:00',
  notifyDaily: false,
  notifyOutliers: false,
  // WHAT YOU SET AGAINST YOURSELF. All four are null as shipped: an app that
  // invents a limit nobody asked for is an app whose limits get ignored.
  // `maxStake` is the most any one bet may be, `maxDaily` the most a Sydney day
  // may total; `pendingLimits` holds a RAISE that has not fallen due yet (24
  // hours — see lib/limits.js), and `breakUntil` is the first day you are not on
  // a break, as a bare 'YYYY-MM-DD'. A break cannot be ended early, so nothing
  // in this file clears it.
  maxStake: null,
  maxDaily: null,
  pendingLimits: null,
  breakUntil: null,
  // WHAT YOUR BOOKS WILL TAKE FROM YOU, which is a different question entirely:
  // `{ [bookmakerKey]: { maxBet, limited, limitedAt } }`, empty as shipped.
  // Empty means the racing minimum-bet schedules apply as the default and sport
  // has no cap at all (lib/limits.js), which is the honest starting point —
  // every account is unlimited until a book decides otherwise.
  bookLimits: {},
  // Comma-joined: the plan schema has no list type.
  tiersShown: TIER_ORDER.join(','),
  // Which strategies you have taken OFF Today, by id. Empty is the app as
  // shipped: every strategy runs and every one of them prints.
  hidden: [],
  // May a NEW set use a match that has already kicked off? Off as shipped: a
  // bookmaker will not take a multi with a leg under way without a phone call,
  // so a set that deals one is a set that cannot be placed. On is for the
  // punter who is happy to ring it through. See `dealCutoff` below.
  allowStarted: false,
};

/**
 * The saved profile, with the retired `pad` key left behind.
 *
 * The pad used to hold up to four chosen strategies and Today printed those.
 * Every strategy prints now, so a stored `pad` is not a smaller version of the
 * new setting — it is a list of the ones somebody picked out, which says
 * nothing at all about which ones they would want hidden. Migrating it either
 * way would be inventing an answer, so it is ignored and drops out the next
 * time the profile is written.
 */
function storedProfile() {
  if (isDemo.value) return {};
  const { pad: _pad, ...rest } = prefs.riskProfile ?? {};
  return rest;
}

export const riskProfile = ref({ ...PROFILE_DEFAULTS, ...storedProfile() });
export const riskProfiles = computed(() => [riskProfile.value]);

/** tiersShown as a list, in tier order. Never empty: an empty list shows all four. */
export const tiersShown = computed(() => {
  const picked = String(riskProfile.value.tiersShown ?? '').split(',');
  const list = TIER_ORDER.filter((t) => picked.includes(t));
  return list.length ? list : TIER_ORDER;
});

/**
 * The strategies you have taken off Today, by id. Never null.
 *
 * Canonicalised on the way out, which is why nothing downstream has to think
 * about it: three edge strategies merged into Market edge, and a profile that
 * hid one of them meant to hide the strategy it became. See
 * `lib/engine/strategies/aliases.js`.
 */
export const hiddenIds = computed(() =>
  (Array.isArray(riskProfile.value.hidden) ? riskProfile.value.hidden : []).map(canonicalStrategyId));

/**
 * EVERY strategy the app can run: the catalogue, then yours by name.
 *
 * Classic is first and stays first — it is the app as shipped, and the section
 * a reader has a habit about does not move around by value. The rest of the
 * catalogue keeps its own order, which is the order it was written in.
 *
 * Each one comes out as a bare DOCUMENT: a saved strategy is stored with
 * bookkeeping on it (entity, baseId, updatedAt) and `validateStrategy` rejects
 * every key it does not know, so a row handed straight to the engine would run
 * as "invalid strategy" and print nothing.
 */
export const allStrategyDocs = computed(() => [
  CLASSIC,
  ...CATALOGUE.filter((s) => s.id !== CLASSIC.id),
  ...[...myStrategies.value].sort(byName),
].map((s) => toDocument(s)));

/**
 * Which strategy ids deal from the RACING CARD rather than from the sports
 * board — read off each document (`isRacingStrategy`), so a punter's fork of
 * Racing value is in the set for the same reason the builtin is.
 *
 * One answer, because three things now ask: the card's own watcher, the stamp
 * that says where a ticket's prices came from, and the dedupe behind them.
 */
export const racingStrategyIds = computed(() =>
  new Set(allStrategyDocs.value.filter(isRacingStrategy).map((s) => s.id)));

/**
 * The strategies SHOWN on Today: every one of them, less the ones you hid.
 *
 * Hiding the last one is refused where the hiding happens
 * (`stores/strategies.js`), so this fallback is for a profile that arrived
 * hiding everything anyway — a stored list from another device, a strategy
 * deleted out from under it. Today has to print something, and Classic is what
 * it prints.
 */
export const padStrategies = computed(() => {
  const shown = allStrategyDocs.value.filter((s) => !hiddenIds.value.includes(s.id));
  return shown.length ? shown : [toDocument(CLASSIC)];
});

/**
 * The profile, changed here and written through — except on a board nobody is
 * paying for. /demo and the sample day (see `sampleMode`) both keep the change
 * in memory so the screen answers the tap, and neither of them leaves anything
 * behind: settings made against a borrowed board are settings about somebody
 * else's fixtures.
 */
export function saveRiskProfile(patch) {
  riskProfile.value = { ...riskProfile.value, ...patch };
  if (!isDemo.value && !sampleMode.value) prefs.riskProfile = { ...riskProfile.value };
  return riskProfile.value;
}

/**
 * Turn "may a new set use a match already under way" on or off, and move the
 * deal cutoff with it.
 *
 * One function because it is one decision. Switching the rule ON — that is,
 * `allow` false — has to move the cutoff to now, or the setting would not take
 * effect until the next thing that moves it and the switch would read as
 * broken. Switching it off moves nothing: the cutoff is still where it was, and
 * `dealAsOf` simply stops consulting it.
 */
export function setAllowStarted(allow) {
  const on = allow === true;
  if (!on) dealCutoff.value = now.value;
  return saveRiskProfile({ allowStarted: on });
}

// --- the limits, and the break ------------------------------------------------
// The rules are all in `lib/limits.js`. What is here is the profile they read,
// the clock they read it at, and the ONE place a bet is refused.

/**
 * The limits as they stand right now: the saved pair, with a raise folded in
 * once its 24 hours are up. Read this, never `riskProfile.maxStake` — the
 * stored pair is what was last written, not what is in force.
 *
 * It hangs off `now`, which ticks every minute, so a raise that falls due while
 * the app is open arrives without anything having to watch for it.
 */
export const limits = computed(() => liveLimits(riskProfile.value, now.value));

/** On a break? True until the Sydney day `breakUntil` names arrives. */
export const onBreak = computed(() => onBreakAt(riskProfile.value, now.value));

/** What today's bets have already cost, on the Sydney day the limit counts. */
export const todayStaked = computed(
  () => stakedOn(betRecords.value, localDate(now.value, SYDNEY)),
);

/**
 * Set the limits. Lower applies now; higher, or none at all, in 24 hours.
 *
 * The pair handed in is what the two boxes say, and the pair compared against
 * is what is in FORCE — so a raise that has already fallen due counts as the
 * current limit rather than being raised from twice.
 */
export function setLimits({ maxStake, maxDaily } = {}) {
  return saveRiskProfile(applyLimitChange(limits.value, { maxStake, maxDaily }, Date.now()));
}

/**
 * Take a break of `days` days. It ends at the start of the Sydney day it names
 * and NOT BEFORE — there is no function here that shortens one, and a shorter
 * break asked for during a longer one leaves the longer one standing.
 *
 * The notifications go with it, both the setting and the schedule already on the
 * device: a break with a reminder still firing is the app asking somebody back
 * every morning of it. `notify.js` is imported late for the reason every other
 * late import here is — it reaches for `window.Capacitor`.
 */
export function takeBreak(days) {
  const profile = saveRiskProfile(startBreak(riskProfile.value, days));
  import('./core/notify.js')
    .then(({ cancelDaily }) => cancelDaily())
    .catch(() => { /* nothing scheduled, or no plugin: the break stands either way */ });
  return profile;
}

// --- odds-event --------------------------------------------------------------
export const snapshot = ref(null);
/** idle | loading | ready | offline | unconfigured | signed-out | error — see lib/odds.js */
export const oddsStatus = ref('idle');
let inflight = null;

/**
 * A BOARD BEFORE A KEY. True while the server will not price a day for this
 * device — no odds key on it, or nobody signed in yet, which are the same
 * `unconfigured` either way (`lib/odds.js`, and `TodayPage.gate` tells the two
 * apart for the reader).
 *
 * What it does is put the SAMPLE DAY on the pad, so somebody deciding whether
 * this app is worth a signup reads a board rather than a form. What it is not
 * is demo mode: `isDemo` is the `/demo` route, it seeds a whole sample history
 * and it says so in a banner across the top. This is the real app, with the
 * real History, the real Settings and one borrowed board — yesterday's shape
 * printed against today's date.
 *
 * AND IT WRITES NOTHING. Every guard that refuses under `isDemo` refuses here
 * too — the profile, the paper trail, the generations, the records, the
 * notifications — and `markPlaced` throws outright. A sample ticket is not a
 * proposal anybody made, and a trail that scored one would be scoring the
 * fixtures in `lib/demo.js`. Nothing here fetches either; there is nothing to
 * fetch with.
 */
export const sampleMode = computed(() => !isDemo.value && oddsStatus.value === 'unconfigured');

/**
 * What the sample board says when something asks it to write. One sentence, in
 * the store, because the refusal has to read the same whether it arrives as a
 * toast off `markPlaced` or as the line where a button was.
 */
export const SAMPLE_REFUSAL = 'This is a sample board. Add your free Odds API key to bet on today.';

/**
 * The borrowed board itself, held by identity so everything downstream can ask
 * "is this the sample?" rather than re-deriving it from a status that has since
 * moved on. That matters at exactly one moment: a key is pasted, the fetch
 * starts, `oddsStatus` becomes `loading` — so `sampleMode` is already false —
 * and the pad is still printing the sample. See `padNow`.
 */
let sampleBoard = null;
const onSampleBoard = () => sampleBoard !== null && snapshot.value === sampleBoard;

// The sample lands when the server says there is nothing to price for this
// device, and is taken away again the moment anything real is on its way. QA's
// `empty` fixture still wins: the empty state is the screen it asked to
// photograph, and a sample board over the top of it would file a picture of a
// full pad under the name `empty`.
watch(sampleMode, (on) => {
  if (on) {
    snapshot.value = demoSnapshot(Date.now(), qaSeed === 'empty' ? 'empty' : 'sample');
    // READ BACK, not kept from before the assignment: `ref` hands out a deep
    // reactive proxy, so the object that goes in is never the object that comes
    // out and an identity check against the original would never once match.
    sampleBoard = snapshot.value;
  } else if (onSampleBoard()) {
    sampleBoard = null;
    snapshot.value = null;
  }
});

/**
 * YYYY-MM-DD plus n calendar days. String arithmetic on purpose: a bare date is
 * a calendar day, and adding 24 hours to one lands on the wrong day either side
 * of a daylight-saving change.
 */
export function addDay(date, n = 1) {
  const [y, m, d] = String(date).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * Is this board spent — every match on it already under way?
 *
 * At 23:48 in Sydney every Australian match has started, so the window the
 * server fetches is empty and the pad has nothing to print. That is the moment
 * to roll on to tomorrow.
 *
 * A board with no events at all does NOT roll. An empty board is the state
 * before the prices drop as much as the state after the last match starts, and
 * the only thing that tells the two apart is having had a kick-off to be past.
 */
export function shouldRollToTomorrow(snap, at = Date.now()) {
  const kicks = (Array.isArray(snap?.events) ? snap.events : [])
    .map((e) => Date.parse(e?.commence_time ?? ''))
    .filter((t) => Number.isFinite(t));
  return kicks.length > 0 && Math.max(...kicks) <= at;
}

function noteCredits() {
  const c = getOddsCredits();
  if (c) oddsCredits.value = c;
}

/**
 * Make sure the pad has a board. Cheap to call often: the day already on the
 * device answers without a request.
 *
 * Once today is spent this asks for TOMORROW and prints that instead. The
 * server only fetches tomorrow when today really is finished, so the roll costs
 * nothing on an ordinary afternoon.
 */
export function loadToday({ force = false } = {}) {
  if (isDemo.value) {
    snapshot.value = demoSnapshot(Date.now(), qaSeed);
    oddsStatus.value = 'ready';
    return Promise.resolve();
  }
  // QA's empty fixture is the no-markets screen, whatever the server says.
  if (qaSeed === 'empty') {
    snapshot.value = null;
    oddsStatus.value = 'ready';
    return Promise.resolve();
  }
  const date = today.value;
  // Today's board, or — once the pad has rolled — tomorrow's, which is the one
  // the device actually kept. The cache holds ONE day, and after the evening
  // roll that day is tomorrow's: asked for today it answered nothing, so every
  // open of the app blanked the pad and fetched both days again.
  const cached = cachedSnapshot(date) ?? cachedSnapshot(addDay(date, 1));
  if (cached && !force && !shouldRollToTomorrow(cached, now.value)) {
    snapshot.value = cached;
    oddsStatus.value = 'ready';
    return Promise.resolve();
  }
  // A spent board is still worth printing while the network is asked: it
  // names the day's tickets, and their legs read "started". Only a pad with
  // nothing on it at all shows "Reading today's prices".
  if (cached && !snapshot.value) snapshot.value = cached;
  // ON A BREAK NOTHING IS FETCHED — no credits, no board, nothing to bet on.
  // The guard is here rather than at the top of the function on purpose: what
  // the device already holds still prints, because Matches is a scoreboard and
  // reading a score is not a bet. Everything past this line costs money.
  if (onBreak.value) {
    if (snapshot.value) oddsStatus.value = 'ready';
    return Promise.resolve();
  }
  if (inflight) return inflight;
  oddsStatus.value = 'loading';
  inflight = loadBoard(date, cached).finally(() => { inflight = null; });
  return inflight;
}

/**
 * Today's board, or tomorrow's once today is spent.
 *
 * The snapshot is only ever replaced by something real, so a fetch that fails
 * leaves what is on the pad where it is — which is what keeps tomorrow's board
 * printing if the roll happens and the connection then drops.
 */
async function loadBoard(date, cached) {
  const { snapshot: s, status } = await fetchSnapshot(date);
  if (s && shouldRollToTomorrow(s, now.value)) {
    const next = await fetchSnapshot(addDay(date, 1));
    if (next.snapshot?.events?.length) {
      keepBeforeBoard(next.snapshot, 'rolled to tomorrow');
      snapshot.value = next.snapshot;
      oddsStatus.value = next.status;
      noteCredits();
      notifyNewOutliers();
      return;
    }
  }
  if (s && (s.events?.length || !snapshot.value?.events?.length)) {
    // A pull that found a newer board than the one on the pad: the prices
    // under what it is proposing are about to change.
    keepBeforeBoard(s, 'board refreshed');
    snapshot.value = s;
    notifyNewOutliers();
  } else if (!s && cached) snapshot.value = cached;
  oddsStatus.value = status;
  noteCredits();
}

/** The key one mispriced price is remembered by: the side, at that book. */
const outlierKey = (o) => `${o.matchKey}:${o.side}:${o.bookmaker}`;

/** Where the day's already-notified keys live. One row, replaced each day. */
const NOTIFIED_KEY = 'notifiedOutliers';

/**
 * A board just LANDED. Was there anything on it worth interrupting somebody for?
 *
 * Three rules, and each of them is the difference between an alert and a
 * nuisance:
 *
 *   NEVER ON A CACHE HIT. This is only ever called from `loadBoard`, which is
 *   the path that actually fetched — `loadToday` answers from the device
 *   without coming near it. The same prices re-read are not news.
 *
 *   NEVER THE SAME PRICE TWICE. Every outlier is remembered by side and book
 *   for the pad's own day, and only the ones that were not in that set are
 *   notified. A refresh that finds the same four flags nothing; a refresh that
 *   finds a fifth flags one.
 *
 *   NEVER A FETCH FOR ITS OWN SAKE. Nothing here asks for anything. It reads
 *   the universe the punter's fetch just paid for, and that is the only reason
 *   a notification can ever be honest about costing no credit.
 *
 * AND THE RACES, ON ONE FURTHER CONDITION. A mispriced runner is the same
 * finding as a mispriced odd — the same detector, the same thresholds (racing's,
 * per match) and the same prefs switch — so it goes in the same alert rather
 * than into a second one that could buzz a minute later. But the racing card is
 * bought ON DEMAND and cached for twenty minutes, so a board landing at 7am
 * would otherwise notify off a card from last night: `racingFetched` is the
 * guard, and it means "this session has actually bought a card". Without it the
 * races are simply left out and the sports half goes as it always did.
 *
 * A CODE SWITCHED OFF NEVER BUZZES A PHONE, and nothing here has to check:
 * `raceUniverse` is already cut to the codes that are on, so a runner from a
 * code somebody has switched off is not on the board this reads.
 *
 * Fire-and-forget, and it swallows everything: a notification is never worth
 * failing a board over. `notify.js` is imported late for the reason the rest of
 * the late imports here are — it reaches for `window.Capacitor`, and dragging a
 * browser into the node test runner stops whole suites loading.
 */
function notifyNewOutliers() {
  // Neither sample board is news. /demo is a fixture, and the sample day is
  // yesterday's shape — a phone buzzing about a mispriced Collingwood price
  // that was never on a bookmaker's screen this morning is the app lying to
  // somebody who has not even given it a key yet.
  if (isDemo.value || sampleMode.value) return;
  if (riskProfile.value.notifyOutliers !== true) return;

  // `racingOutliers` is the card's own cut — it drops a race the FEED has
  // called in-play, which the clock alone cannot know (see `hasJumped`).
  const races = racingFetched ? racingOutliers(raceUniverse.value, { now: now.value }) : [];
  const found = [...findOutliers(universe.value, { now: now.value }), ...races];
  if (!found.length) return;

  const date = padDate.value;
  const stored = prefs[NOTIFIED_KEY];
  const known = new Set(
    stored?.date === date && Array.isArray(stored.keys) ? stored.keys : [],
  );
  const fresh = found.filter((o) => !known.has(outlierKey(o)));
  if (!fresh.length) return;

  import('./core/notify.js')
    .then(({ notifyOutliers }) => notifyOutliers(fresh))
    .then((sent) => {
      if (sent === false) return;
      for (const o of fresh) known.add(outlierKey(o));
      prefs[NOTIFIED_KEY] = { date, keys: [...known] };
    })
    .catch(() => { /* no notification; the board is still on the pad */ });
}

/**
 * MAY THIS BOARD BE FETCHED AGAIN? Today's or tomorrow's, in Sydney.
 *
 * ONE RULE, IN ONE ZONE, and the server holds the same one (`refreshable` in
 * server/odds.mjs). It used to be `padDate === today`, and `today` here is the
 * DEVICE's day — so the two sides had two different definitions of the word and
 * disagreed twice over. On 2026-09-16 at 6:13 pm Sydney the pad had rolled to
 * the 17th, the owner tapped Refresh, and the request went out (a phone whose
 * own day was already the 17th passed the guard) to be refused by the server
 * with `refresh-not-today`. From the roll until the next morning, every
 * evening, the one deliberate fetch in the app refused the only board on the
 * screen: "Tomorrow's prices can't be refreshed yet", on the prices the punter
 * is actually betting into.
 *
 * Sydney and not the device, because the board is an Australian day: the
 * server's `today` is Sydney's, the drop hour is Sydney's, and a phone in
 * Auckland or one with a fast clock must ask the same question of the same day
 * as a phone in Melbourne. It is the same zone every key in the app is dated in
 * (`APP_TZ` in lib/engine/teams.js); `SYDNEY` is the copy this file already
 * imports, and one more name for one more string would help nobody.
 *
 * Anything past tomorrow is refused, which is the one case left for the
 * `not-today` toast.
 */
export function canRefreshBoard(date, at = now.value) {
  const sydney = localDate(at, SYDNEY);
  return date === sydney || date === addDay(sydney, 1);
}

/**
 * Fetch the board's prices AGAIN, on purpose. The only thing in the app that
 * spends credits without the pad being empty — so nothing calls it on a timer,
 * and the button that does says what it will cost first.
 *
 * Answers `{ ok }` rather than throwing, because every outcome is something to
 * print: what it cost, or how long until it may be asked again.
 *
 * The pad's status is never touched. A refusal must not blank a board that is
 * still perfectly good — the prices on it are simply the ones already there.
 *
 * IT DOES NOT SETTLE, and that is the fix for the bug this comment used to
 * describe the other way round. Settlement used to hang off the end of this
 * function, which meant every refusal above returned BEFORE it: an owner who
 * tapped Refresh at 4:55 pm with the pad on tomorrow got the refusal and
 * nothing else — no settling, and no racing card either, because the caller
 * handed the refusal straight back as its own answer. The three halves of a
 * refresh are independent and `refreshToday` runs all three. This one buys the
 * board and says what happened.
 */
export async function updatePrices() {
  if (isDemo.value) return { ok: false, reason: 'demo' };
  // ON A BREAK NOTHING IS BOUGHT. `canUpdatePrices` has always said so and the
  // bar reads it, but this function never did — it was the roll that stopped a
  // break spending, by accident, because the pad was on tomorrow and tomorrow
  // was refused. Tomorrow is refreshable now, so the rule has to be said here.
  if (onBreak.value) return { ok: false, reason: 'break' };
  // Today's board or tomorrow's, in Sydney, which is the server's rule too.
  // Anything else is a date nothing will sell, and the caller is told rather
  // than a credit being risked on it.
  if (!canRefreshBoard(padDate.value)) return { ok: false, reason: 'not-today' };
  // One fetch at a time, through the same gate `loadToday` uses: a refresh
  // racing an open would pay twice for one board.
  if (inflight) await inflight.catch(() => {});
  inflight = refreshPrices().finally(() => { inflight = null; });
  // Awaited, and read back before the caller settles, so `inflight` is clear by
  // the time settlement starts — a visibilitychange landing mid-settle must not
  // sit behind it waiting for a board it already has.
  return inflight;
}

async function refreshPrices() {
  // The board on the pad, not the calendar's today: after the evening roll that
  // is tomorrow's, and tomorrow's is the board the punter is betting into.
  const { snapshot: s, status, retryAt } = await fetchSnapshot(padDate.value, { refresh: true });
  if (status === 'too-soon') return { ok: false, reason: 'too-soon', retryAt: retryAt ?? null };
  if (status !== 'ready' || !s) return { ok: false, reason: status };
  // The pad is about to re-run on the new prices. What it was proposing at the
  // old ones was a proposal, and this is the last moment it exists.
  keepBeforeBoard(s, 'prices updated');
  snapshot.value = s;
  // A fetch the punter made on purpose is the one most worth a word about
  // what it found; the same dedupe as the boot path, so nothing repeats.
  notifyNewOutliers();
  noteCredits();
  const credits = s.credits ?? oddsCredits.value ?? {};
  return { ok: true, cost: credits.last ?? null, remaining: credits.remaining ?? null };
}

/**
 * Settle whatever has finished since the last sync, on the punter's own key.
 *
 * The complaint this exists for was "I pressed refresh but nothing settled".
 * Refresh fetched the prices and the pad re-ran, but going and FINDING the
 * results of finished games was nobody's job until the next scheduled run — so
 * a bet whose match had been over for hours still read Pending, and from the
 * outside that is indistinguishable from a bet the app cannot settle at all.
 * One tap does both now.
 *
 * It runs whether or not the prices moved. A refresh refused by the server's
 * own floor says nothing about whether last night's game has a score yet, and
 * the punter tapping the button is asking about both. It costs nothing at all
 * when no bet is waiting on anything — see `triggerForegroundSettle`.
 *
 * Never fails the refresh: fresh prices are a good outcome on their own, and
 * the next sync settles the same bets again. Imported late for the same reason
 * `updateStake` does it — settlement.js is built on this file, so a static
 * import back would close a cycle.
 *
 * Answers HOW MANY BETS IT SETTLED, so the toast can say so. Zero where nothing
 * was waiting, and zero where the import or the sync failed — a refresh that
 * could not settle says nothing about settling rather than claiming a number.
 */
async function settleAfterRefresh() {
  try {
    const { syncSettlement } = await import('./lib/stores/settlement.js');
    const out = await syncSettlement({ force: true, settle: true });
    return Number.isFinite(out?.settled) ? out.settled : 0;
  } catch {
    // Nothing settled. What is on the pad is still the new board.
    return 0;
  }
}

if (inBrowser) {
  loadToday();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    now.value = Date.now();
    loadToday();
  });
  // Yesterday's live pad is a proposal too, and the date flipping is itself
  // what makes its board stop being current — so it is kept here, before
  // anything else in the app reacts to the new day. Registered first for that
  // reason.
  watch(today, (d, prev) => {
    keepLivePad('rolled to tomorrow', prev);
    loadToday();
  });
  // Signing in has to bring the board with it.
  //
  // The day is asked for the moment the app boots, which is before Firebase
  // has restored a session, so that first request can be refused — and without
  // this the pad would sit there telling a signed-in person to sign in until
  // they relaunched. The addon emits on every auth state, so this fires both
  // for a restored session and for a fresh sign-in.
  //
  // Imported here rather than at the top of the file: core/state.js registers
  // window listeners as it loads, so a module-scope import drags a browser
  // into the node test runner and three suites stop loading altogether.
  import('./core/state.js')
    .then(({ bus }) => bus.on('auth', (user) => { if (user) loadToday({ force: true }); }))
    .catch(() => {});
  // A Betfair session is twelve hours and keepAlive is what pushes that out,
  // so the one moment worth spending it is the app opening. Costs nothing,
  // answers false and does nothing at all when there is no session to keep.
  import('./lib/betfair.js').then(({ keepSessionAlive }) => keepSessionAlive()).catch(() => {});
}

/**
 * The board on the pad is one worth printing: today's, or tomorrow's once today
 * is spent. Anything older is a day that has been and gone.
 */
const snapshotIsCurrent = computed(() => {
  const s = snapshot.value;
  if (!s) return false;
  if (isDemo.value) return true;
  return s.forDate === today.value || s.forDate === addDay(today.value, 1);
});

/** The date the pad is printing — tomorrow's, when today is spent. */
export const padDate = computed(() =>
  (snapshotIsCurrent.value && !isDemo.value ? snapshot.value.forDate : today.value));

/** What is on the pad is tomorrow's board, so the dateline can say so. */
export const showingTomorrow = computed(() => padDate.value !== today.value);

/**
 * Today is finished and there is nothing to roll to yet: every match has
 * started and tomorrow's board is not out. Today prints the honest reason
 * rather than "no markets open yet", which reads as "the day hasn't started".
 */
export const todaySpent = computed(() =>
  !!snapshot.value && snapshot.value.forDate === today.value
  && shouldRollToTomorrow(snapshot.value, now.value));

/** Today's matches, priced by the bookmaker in the risk profile. */
export const oddsEvents = computed(() => {
  if (!snapshotIsCurrent.value) return [];
  const s = snapshot.value;
  return normaliseOdds(s.events ?? [], riskProfile.value.bookmaker, s.fetchedAt);
});

export const hasMarkets = computed(() => oddsEvents.value.length > 0);

/** Bookmakers that priced something today, or every AU one before the drop. */
export const availableBookmakers = computed(() => {
  const keys = new Set();
  for (const e of snapshot.value?.events ?? []) for (const b of e.bookmakers ?? []) keys.add(b.key);
  return keys.size ? BOOKMAKERS.filter((b) => keys.has(b.key)) : BOOKMAKERS;
});

// --- credits -----------------------------------------------------------------
export const oddsCredits = ref(getOddsCredits());

/**
 * When the prices on the pad were fetched. Null before the drop, when there is
 * a board but nothing has been paid for yet.
 */
export const pricesFetchedAt = computed(() => snapshot.value?.fetchedAt ?? null);

/**
 * A different fetch of the board is a new deal: move the cutoff (event (a)).
 *
 * Watched on the STAMP, not on the snapshot object, which is what makes a
 * `loadToday()` cache hit free — it re-assigns the same day's cached board, the
 * stamp does not change, and the pad somebody left open is the pad they come
 * back to. A refetch, a pull that found a newer shared board and the roll to
 * tomorrow all carry a stamp of their own and all move it.
 *
 * Registered at module scope rather than inside the `inBrowser` guard, and
 * flushed synchronously: it writes one ref, it must land before the board it
 * belongs to is read, and a store test has no browser to run the guarded half.
 * `keepLivePad` still keeps the old pad as it was, because every caller keeps
 * BEFORE assigning `snapshot.value` — see `keepBeforeBoard`.
 */
watch(pricesFetchedAt, (at, was) => {
  if (at && at !== was) dealCutoff.value = now.value;
}, { flush: 'sync' });

/**
 * Is fetching the board's prices again on offer?
 *
 * One rule in one place, because two things ask: the toolbar button on Today
 * and the confirm behind it. The demo never fetches anything, a break buys
 * nothing at all, and before the drop there is nothing to fetch AGAIN. WHICH
 * DAY it is is a separate question with a separate answer — `canRefreshBoard`,
 * today or tomorrow in Sydney — because the pad rolls to tomorrow every evening
 * and that board is refreshable. `loading` still counts once there is a board:
 * a pull-to-refresh flips the status for a moment, and a control that vanished
 * and came back would shift the bar under the thumb that pulled it.
 */
// Whichever board the pad is on — today's, or tomorrow's once today is
// spent. It used to refuse once the pad had rolled, which was every
// afternoon: the owner's most deliberate fetch vanished from the toolbar for
// half of every day, and the feed prints tomorrow's tickets from that board.
// And never on a break. This is the one deliberate fetch in the app, so the
// rule lives with the other two rather than as a `v-if` on the toolbar: both
// the button and the confirm behind it read this, and a break must not leave
// either of them able to spend a credit.
// And never on the sample board, which carries a `fetchedAt` of its own and
// would otherwise pass the test below: there is no key behind it to spend.
export const canUpdatePrices = computed(() =>
  !isDemo.value
  && !sampleMode.value
  && !onBreak.value
  && !!pricesFetchedAt.value
  && (oddsStatus.value === 'ready' || oddsStatus.value === 'loading'));

// --- racing ------------------------------------------------------------------
//
// A SECOND BOARD, AND IT IS FETCHED BY A DIFFERENT RULE. The sports board drops
// once a morning and every device shares it; a racing card is 2 credits and
// changes all day, so this one is bought a few times a day on this device's own
// key — and NEVER, EVER ON A TIMER. Nothing below registers a watcher that
// fetches, and nothing here polls. The whole 1,500-credit month depends on that
// staying true.
//
// IT FOLLOWS THE BOARD NOW, which is the owner's complaint of 2026-09-16: "the
// racing prices don't refresh unless you go into the Racing tab and refresh".
// Everything else on the pad lands at boot, again when the app comes back to
// the foreground, and again on a Refresh; the card was bought on a tab enter
// and a pull and on nothing else, so a racing ticket printed at 9:30 was still
// quoting 9:30's prices at four in the afternoon. `autoLoadRacing` is that
// cadence, and the BUDGET it runs inside is `lib/racing/auto.js`: a
// PuntersEdge key, a code switched on, the Sydney hours 06:00–23:30, a card
// over an hour old, and at most six automatic pulls a day, counted in prefs.
// Twelve credits a day, 360 a month of the free 1,500.
//
// The other three rules, all of them here rather than in the page that shows
// the card: a cached card prints first and is only refetched past
// RACING_MAX_AGE_MS or on an explicit pull; /demo and the sample board show the
// demo card READ-ONLY and fetch nothing; and a break fetches nothing at all,
// for the same reason `loadToday` stops at the same line — everything past it
// costs money.

/** How old a card may be before an open of the tab is worth 2 credits. */
export const RACING_MAX_AGE_MS = 20 * 60 * 1000;

/**
 * What PuntersEdge last said was left on the racing key: `{ remaining, used }`.
 *
 * A SECOND POOL, AND IT IS NOT THE BOARD'S. `oddsCredits` is The Odds API's 500
 * and this is PuntersEdge's 1,500; they are different keys, different months
 * and different arithmetic, and the Refresh confirm spends out of both at once.
 * The meter rides every racing answer (`ask` in lib/racing/fetch.js), so it is
 * read rather than fetched. Null until this device has asked for anything.
 */
export const racingCredits = computed(() => providerCredits.value?.[RACING_PROVIDER] ?? null);

/** `{ forDate, fetchedAt, races, credits }`, as GET /api/racing answers it. */
export const racingSnapshot = ref(null);

/**
 * The day's free second opinions: `{ forDate, fetchedAt, sports }`, as
 * GET /api/racing/features answers it.
 *
 * BESIDE THE CARD AND NEVER INSIDE IT, the same rule `racingMovers` follows: a
 * rating is somebody else's opinion of a runner and the card is what the
 * bookmakers are offering on it, and folding one into the other would make a
 * stored card carry a claim nobody quoted. It is free, so it rides every card
 * load rather than only an explicit pull.
 */
export const racingFeatures = ref(null);

// --- the codes a device deals from --------------------------------------------
//
// Horses, greyhounds and harness, switched on and off in Settings the way the
// sports board's leagues are. A REF rather than a read of prefs at the point of
// use, for `racingKeyed`'s reason below: the card, the strategies, the builder
// and the Racing tab all read it, and a plain storage read would leave every one
// of them printing yesterday's answer until something unrelated re-rendered.
//
// The RULE is `lib/racing/codes.js` and none of it is here: this holds the
// record, writes it through, and is the one place that knows a switch is worth
// a fetch.

/** The three switches, as this device has them. Never empty — see `setCode`. */
export const racingCodes = ref(normaliseCodes(isDemo.value ? undefined : prefs[RACING_CODES_PREF]));

/** The sports a racing strategy may look at today: the codes that are on. */
export const enabledRacingSports = computed(() => codesToSports(racingCodes.value));

/**
 * Switch one code on or off. Answers the refusal — 'last', 'unknown' or null —
 * so Settings can say what happened rather than leaving a toggle to spring back.
 *
 * WRITTEN THROUGH, EXCEPT ON A BOARD NOBODY IS PAYING FOR. The same rule
 * `saveRiskProfile` follows: /demo and the sample day keep the change in memory
 * so the screen answers the tap, and neither leaves anything on the device.
 *
 * SWITCHING ONE ON DROPS THE CACHED CARD. The card on the device was bought for
 * the codes that were on when it was bought, so the greyhounds somebody has
 * just asked for are not in it and no amount of filtering will put them there.
 * Forgetting it makes the next tab enter buy one — two credits, the ordinary
 * price of a card, and the help text in Settings says so. Switching one OFF
 * spends nothing: the card already on the device is simply read shorter.
 */
export function setRacingCode(code, on) {
  const { codes, refused } = setCode(racingCodes.value, code, on);
  if (refused) return refused;
  const widened = on && !racingCodes.value[code];
  racingCodes.value = codes;
  if (isDemo.value || sampleMode.value) return null;
  try { prefs[RACING_CODES_PREF] = { ...codes }; } catch { /* private mode */ }
  if (widened) {
    clearRacingCard();
    racingSnapshot.value = null;
  }
  return null;
}

// idle | loading | ready | offline | unconfigured | signed-out | error.
// Deliberately not exported yet: R3 owns the screen that has something to say
// about each of these, and an export nothing reads is an export nobody
// maintains. `canRefreshRacing` is the only thing that needs it today.
const racingStatus = ref('idle');
let racingInflight = null;

/**
 * Whether this device had a PuntersEdge key the last time anything asked.
 *
 * A ref rather than a call to `getProviderKey` inside `canRefreshRacing`,
 * because the key lives in plain storage and nothing watches it: a computed
 * that read it directly would be cached from before the key was pasted, and
 * the Refresh button would stay missing until something unrelated happened to
 * invalidate it. `noteRacingKey` re-reads it, and every path that is about to
 * decide whether to spend calls that first — a tab enter, an automatic pull,
 * and the Refresh button and the confirm behind it.
 */
const racingKeyed = ref(false);

/** Re-read the key. Cheap, and the answer can change between two taps. */
function noteRacingKey() {
  racingKeyed.value = !!getProviderKey(RACING_PROVIDER);
  return racingKeyed.value;
}

/**
 * Has a racing card actually been FETCHED in this session?
 *
 * Not a ref and not stored: it is the difference between a card this device
 * bought a minute ago and one it read off the disk on the way in, and it is
 * what `notifyNewOutliers` asks before it lets a runner buzz a phone. A
 * notification about a price is a promise the price is up now, and a cached
 * card is a photograph of one — twenty minutes old at the outside, which is
 * three races on a Saturday.
 */
let racingFetched = false;

/** Is this card old enough to be worth buying again? */
function racingStale(card, at) {
  const fetchedAt = Date.parse(card?.fetchedAt ?? '');
  return !Number.isFinite(fetchedAt) || at - fetchedAt >= RACING_MAX_AGE_MS;
}

/**
 * Make sure the Racing tab has a card. Called on tab enter and on a pull, and
 * from nowhere else.
 *
 * `force` is the pull: it spends 2 credits whatever the cache says. Without it
 * a card younger than twenty minutes answers from the device and costs nothing,
 * which is what makes flicking between tabs free.
 */
/**
 * The sample card, made ONCE.
 *
 * `demoRaceCard` builds its races relative to the clock it is handed, so a
 * second call two minutes later is a second card: different start times,
 * different ids downstream, and a feed that re-deals itself every time
 * something reads it. Held here so the demo board and the demo pad are looking
 * at the same six races all session.
 */
let sampleCard = null;
const demoCard = () => (sampleCard ??= demoRaceCard(Date.now()));

/**
 * The day's free ratings, once per session per day.
 *
 * FREE, so it rides the card rather than a pull — but it is still a request, so
 * it is made once: the Hub publishes a day's file in the morning and does not
 * revise it, and a second fetch would answer the same object. A failure is
 * swallowed whole; a card with no second opinion on it is the ordinary card.
 */
let featuresFor = null;
function loadRacingFeatures(date) {
  if (featuresFor === date) return Promise.resolve();
  featuresFor = date;
  return fetchRacingFeatures(date)
    .then(({ features }) => { if (features) racingFeatures.value = features; })
    .catch(() => { featuresFor = null; });
}

export function loadRacing({ force = false } = {}) {
  if (isDemo.value || sampleMode.value) {
    // Read-only, exactly like the sports sample: there is no key behind it, so
    // there is nothing to fetch and nothing that may be bet.
    if (!racingSnapshot.value) racingSnapshot.value = demoCard();
    // The sample's own ratings, so the rating line is on the sample card too:
    // it is the one thing on that page a screenshot cannot show without them.
    if (!racingFeatures.value) racingFeatures.value = demoRaceFeatures(Date.now());
    racingStatus.value = 'ready';
    return Promise.resolve();
  }
  noteRacingKey();
  const date = localDate(now.value, RACING_TZ);
  const cached = cachedRacingCard(date);
  if (cached && !racingSnapshot.value) racingSnapshot.value = cached;
  if (cached && !force && !racingStale(cached, now.value)) {
    racingStatus.value = 'ready';
    return Promise.resolve();
  }
  // ON A BREAK NOTHING IS FETCHED. What the device already holds still prints —
  // a card is a scoreboard, and reading one is not a bet.
  if (onBreak.value) {
    if (racingSnapshot.value) racingStatus.value = 'ready';
    return Promise.resolve();
  }
  if (racingInflight) return racingInflight;
  racingStatus.value = 'loading';
  // THE CODES RIDE ON THE ONE PULL. A card for two codes is the same two
  // credits as a card for three — `categories=` narrows what comes back, it
  // does not multiply the calls — so a punter who follows the gallops alone
  // pays what everybody pays and carries a shorter card for it.
  // Free and beside the card, not inside it: see `racingFeatures`.
  loadRacingFeatures(date);
  racingInflight = fetchRacingCard(date, { categories: codesParam(racingCodes.value) })
    .then(({ card, status }) => {
      racingStatus.value = status;
      // Only ever replaced by something real, so a fetch that fails leaves the
      // card on the screen where it is.
      if (card?.races?.length) {
        racingSnapshot.value = card;
        // THIS DEVICE HAS BOUGHT A CARD TODAY, which is the one thing that lets
        // a mispriced runner buzz a phone — see `notifyNewOutliers`.
        racingFetched = true;
        notifyNewOutliers();
      }
    })
    .catch(() => { racingStatus.value = 'error'; })
    .finally(() => { racingInflight = null; });
  return racingInflight;
}

// --- the card follows the board ------------------------------------------------
//
// The cadence, and the receipt for it. The RULE is `lib/racing/auto.js`; what is
// here is the record it is asked about and the one function that spends.

function readAutoTally() {
  try { return prefs[RACING_AUTO_PREF] ?? null; } catch { return null; }
}

/**
 * The day's automatic pulls, as the ⓘ in Settings prints them.
 *
 * Read off the device at import and re-read on every pass of `autoLoadRacing`,
 * so a phone left open across midnight in Sydney shows the new day's count
 * rather than yesterday's — the tally is filed under the MEETING's day, not the
 * device's.
 */
export const racingPullsToday = ref(autoPullsOn(readAutoTally(), localDate(now.value, RACING_TZ)));

/**
 * COUNTED WHEN IT IS DECIDED, NOT WHEN IT LANDS. A pull that failed may still
 * have reached the upstream and cost a credit, and a tally that only counted
 * the answers would let a bad morning retry six times on every resume.
 */
function noteAutoPull(at) {
  const next = nextAutoPulls(readAutoTally(), localDate(at, RACING_TZ));
  racingPullsToday.value = next.pulls;
  try { prefs[RACING_AUTO_PREF] = next; } catch { /* private mode */ }
}

/**
 * BUY THE CARD IF IT IS TIME TO, and otherwise do nothing at all.
 *
 * Called from the three places `loadToday` is called from — boot, the app
 * coming back to the foreground, and an enter of Today — and from nowhere else.
 * Never on a timer.
 *
 * The three refusals that are about this DEVICE rather than about the card come
 * first and are the same three `loadRacing` makes for itself: the demo and the
 * sample board have no key behind them, and a break buys nothing at all. Every
 * other clause — the key, the codes, the hours, the staleness, the day's cap —
 * is `shouldAutoPull`, where it can be read as one rule and tested without a
 * device.
 *
 * Answers whether it spent, which is what the tests ask and what nothing on a
 * screen needs: an automatic pull is not an event a punter is told about. The
 * card landing is (the watcher below deals the racing tickets off it), and the
 * dateline on Racing says when the prices are from, as it always has.
 */
export function autoLoadRacing({ at = now.value } = {}) {
  if (isDemo.value || sampleMode.value || onBreak.value) return Promise.resolve(false);
  noteRacingKey();
  const date = localDate(at, RACING_TZ);
  const stored = readAutoTally();
  racingPullsToday.value = autoPullsOn(stored, date);
  const cached = cachedRacingCard(date);
  // THE CARD ON THE DEVICE COMES BACK FOR NOTHING. A reload (an OTA update, a
  // cold start) left `racingSnapshot` empty, and when the cached card was still
  // fresh nothing below put it back: no pull was due, so Today's builder offered
  // no racing at all until somebody opened the Racing tab. Reading the cache
  // costs no credit, so it happens whether or not a pull is.
  if (cached && !racingSnapshot.value) {
    racingSnapshot.value = cached;
    racingStatus.value = 'ready';
  }
  // A card too big for localStorage is in IndexedDB; read it back before
  // deciding whether a pull is due, so a fresh card is not bought twice.
  if (!racingSnapshot.value) {
    return storedRacingCard(date).then((card) => {
      if (card && !racingSnapshot.value) {
        racingSnapshot.value = card;
        racingStatus.value = 'ready';
      }
      return pullIfDue(at, date, stored);
    });
  }
  return pullIfDue(at, date, stored);
}

/**
 * NO CARD AT ALL IS NOT A CARD THAT IS MERELY OLD. The daily cap exists so a
 * card that is on the phone is not re-bought every time the app comes to the
 * front; it was also stopping the FIRST card after a reload once six pulls had
 * been spent, and Today then offered no racing for the rest of the evening
 * (2026-09-26, twice). So a phone with nothing to show gets one rescue pull per
 * session, past the cap but inside racing hours and with a key; a failed rescue
 * is not retried until the app is next started.
 */
let rescuePulled = false;
export function _resetRescuePull() { rescuePulled = false; }

function pullIfDue(at, date, stored) {
  const card = racingSnapshot.value ?? cachedRacingCard(date);
  const codes = enabledCodes(racingCodes.value);
  let go = shouldAutoPull({ at, card, keyed: racingKeyed.value, codes, stored });
  if (!go && !card && !rescuePulled && racingKeyed.value && codes.length && withinRacingHours(at)) {
    rescuePulled = true;
    go = true;
  }
  if (!go) return Promise.resolve(false);
  noteAutoPull(at);
  // `loadRacing` and not `refreshRacing`: the card is 2 credits and the movers
  // are 3, and a fetch nobody asked for does not get to buy the expensive half.
  // Its own twenty-minute cache gate cannot refuse this — nothing is stale to
  // `shouldAutoPull` until it is an hour old.
  return loadRacing().then(() => true);
}

// AND THE CARD LANDS WITH THE BOARD. Boot and the app coming back to the
// foreground are the two moments `loadToday` reads the board, and from
// 2026-09-16 they are the two moments the card is bought as well — inside the
// budget above, and never on a timer. The third is an enter of Today, which is
// the page's own call (`TodayPage.vue`).
//
// A SECOND LISTENER RATHER THAN A LINE IN THE FIRST ONE, and the reason is
// module order: the block that boots the board runs where it stands, hundreds
// of lines above the refs this function reads, and touching them from up there
// is a temporal-dead-zone crash at import. It registers at the same event for
// the same reason — on a phone, a Capacitor WebView fires `visibilitychange`
// when the app returns to the foreground, which is why `loadToday` has never
// needed an `appStateChange` of its own.
if (inBrowser) {
  autoLoadRacing();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    autoLoadRacing();
  });
}

/**
 * Pull to refresh. The one call that spends credits on a card we already have —
 * and now the only call that ever buys MOVERS.
 *
 * Five credits together, and both halves need somebody to have asked: the card
 * is two and the movers are three. A tab enter buys neither (`loadRacing` alone,
 * off the cache where it can), nothing here runs on a timer, and the movers are
 * not fetched at all where the card cannot be — no key, a break, the demo.
 *
 * The movers ride BEHIND the card rather than beside it. A mover is a claim
 * about a runner on the card, so a fresh set of arrows over a stale field is
 * the one order that could put an arrow on a race that is no longer there.
 */
export function refreshRacing() {
  return loadRacing({ force: true }).then(() => loadRacingMovers());
}

/**
 * REFRESH TODAY, which is the one gesture the pad has: what pull-to-refresh
 * does, and what the toolbar's button does too.
 *
 * ONE ACTION, NOT TWO. The toolbar used to carry "Update prices" beside a pull
 * that re-read the same board, two gestures that looked identical and did
 * different things. The owner's complaint was the ordinary one: reaching the
 * pull means scrolling a feed back to the top. So the button IS the pull.
 *
 * AND IT BUYS THE PRICES. The first version of this called `loadToday({ force:
 * true })`, which only re-reads the day the SERVER has already stored: a tap
 * moved nothing, said "these are the newest prices", and the punter's own key
 * was never used. `updatePrices` is the call that actually fetches — it is the
 * one that knows about `too-soon`, keeps the pad's proposals before the board
 * changes under them, notes the credits and notifies what the new prices threw
 * up — so it is the call a refresh makes.
 *
 * THREE HALVES, AND NOT ONE OF THEM MAY CANCEL ANOTHER. This is the bug the
 * owner hit at 4:55 pm Sydney. (The refusal in it no longer happens at all: a
 * rolled pad is refreshable now, on both sides, and `canRefreshBoard` above
 * carries that rule. What the three halves are for outlives it — the floor
 * still refuses, a break still buys nothing, and neither may cancel a settle.)
 * The pad had rolled to tomorrow, so the odds half
 * was refused with `not-today` — and because that refusal came back out of
 * `updatePrices` BEFORE its own settle, and this function handed it straight on
 * as the whole answer, the racing card he had bought at 9:30 that morning was
 * never refreshed (every racing ticket still stamped STALE) and not one of his
 * pending bets was settled. A refusal to sell tomorrow's board says nothing
 * whatever about a racing card or about whether last night's game has a score.
 *
 * So all three run, independently, in the order that makes them true:
 *
 *   THE BOARD, bought again through the punter's key. Its answer is collected —
 *   refusals included — and carried out so the screen can say what happened
 *   rather than guessing.
 *
 *   THE CARD, wherever racing is switched on at all — a key and a code, and no
 *   break. It used to be "only where this device has already bought one today",
 *   which made sense while the card was bought on a tab enter and nowhere else:
 *   a Refresh could not be the FIRST thing to start racing spending. The card
 *   follows the board now (`autoLoadRacing`), so a phone that has not opened
 *   the Racing tab since breakfast still has a card, still prints racing
 *   tickets off it, and the one deliberate fetch in the app has to refresh
 *   them. Asked whatever the odds half answered.
 *
 *   THE SETTLEMENT, exactly once, at the end. It used to hang off the end of
 *   `updatePrices`, which is what made it skippable; it is here now, past both
 *   fetches, and `updatePrices` no longer settles at all — so a refresh cannot
 *   settle twice, and cannot fail to settle because the board was refused.
 *
 * The answer is the odds result plus what the other two halves did: `racing` is
 * whether the card was bought again, `settled` how many bets it settled. The
 * bar composes one sentence out of the three (`PricesBar.vue`'s `said`).
 */
export async function refreshToday() {
  const result = await updatePrices();
  noteRacingKey();
  const racing = racingEnabled.value;
  if (racing) await refreshRacing();
  const settled = await settleAfterRefresh();
  return { ...result, racing, settled };
}

/** The card on its own, which is what an automatic pull buys. */
export const RACING_CARD_CREDITS = 2;

/** The arrows, which only ever ride a deliberate Refresh. */
export const RACING_MOVERS_CREDITS = 3;

/** The card and its movers, together: 2 credits and 3. */
export const RACING_REFRESH_CREDITS = RACING_CARD_CREDITS + RACING_MOVERS_CREDITS;

/**
 * What a refresh will spend, in credits, split into the two halves it buys:
 * `{ odds, racing, total }`.
 *
 * READ AT THE TAP, not watched: it is the answer to "what is this about to
 * cost", and the confirm that prints it is opened by the same tap.
 *
 * ODDS is one credit per sport on the board — `refreshEstimate`, which is what
 * the old Update prices confirm printed, and what the server pays: `rankSports`
 * prices the boards that have matches and skips the rest. Zero where the prices
 * cannot be refetched at all (the demo, the borrowed board, a break, or a date
 * past tomorrow), because then nothing is bought.
 *
 * IT COUNTS THE EVENING'S ROLL, which is the other half of the 6:13 pm bug.
 * This used to read `padDate === today` and answer zero on a rolled pad, which
 * was true only because the fetch itself was refused there. The refresh buys
 * tomorrow's board now (`canRefreshBoard`, and the same rule on the server), so
 * the confirm has to name what that costs: a tap that spends and a confirm that
 * says it will spend nothing is the app lying about the one thing it stops to
 * ask about.
 *
 * RACING is the card plus its movers, wherever racing is switched on at all —
 * the same rule the fetch above follows, because a confirm that counts a
 * different set of things from the tap behind it is the app lying about the one
 * thing it stops to ask about. It used to be "the card this device has already
 * bought once today"; the card follows the board now, so that is no longer what
 * a Refresh buys.
 *
 * COUNTED IN ITS TWO HALVES, `card` and `movers`, because they are bought for
 * different reasons and the confirm names both: two credits are what the app
 * spends on its own a few times a day, and the other three are what this tap
 * adds. `racing` is their sum, which is what the tap will actually spend.
 *
 * THE TWO HALVES ARE INDEPENDENT, which is the whole reason they are counted
 * separately. A pad on a date nothing will sell buys no odds and still buys the
 * card, so the total is five and the confirm asks about five.
 */
export function refreshCost() {
  const odds = canUpdatePrices.value && canRefreshBoard(padDate.value) ? refreshEstimate.value : 0;
  noteRacingKey();
  const on = racingEnabled.value;
  const card = on ? RACING_CARD_CREDITS : 0;
  const movers = on ? RACING_MOVERS_CREDITS : 0;
  return { odds, card, movers, racing: card + movers, total: odds + card + movers };
}

// --- movers ------------------------------------------------------------------
//
// THREE CREDITS, AND ONLY EVER FROM `refreshRacing`. Nothing else in the app may
// call this: it is the most expensive single call the racing tab can make and it
// tells nobody anything they cannot live without, so it rides the one gesture
// that already means "spend something and get me today".
//
// The rows are stored beside the card — `{ movers, fetchedAt }` — and never
// merged into it. A mover is about a price's HISTORY and the card is about its
// present; folding the two would put a figure on the board that no book is
// currently offering.

/** `{ movers, fetchedAt }` as GET /api/racing/movers answered it, or null. */
export const racingMovers = ref(null);

/** The sample movers, made once, for the same reason the sample card is. */
let sampleMovers = null;
const demoMovers = () => (sampleMovers ??= demoRaceMovers(Date.now()));

function loadRacingMovers() {
  if (isDemo.value || sampleMode.value) {
    racingMovers.value ??= { movers: demoMovers(), fetchedAt: new Date(now.value).toISOString() };
    return Promise.resolve();
  }
  // The same three refusals the card makes, in the same order and for the same
  // reasons — and the movers are the half that is dropped first, because the
  // card is the screen and the arrows are a footnote on it.
  if (!racingKeyed.value || onBreak.value) return Promise.resolve();
  // The same codes the card was asked for: an arrow on a race this device is no
  // longer carrying is a footnote on nothing.
  return fetchRacingMovers({ categories: codesParam(racingCodes.value) })
    .then(({ movers, status }) => {
      // Only ever replaced by an answer. A refresh that could not reach the
      // movers leaves this morning's arrows where they are rather than
      // silently clearing a screen that was right.
      if (status === 'ready') {
        racingMovers.value = { movers, fetchedAt: new Date(now.value).toISOString() };
      }
    })
    .catch(() => { /* the card is on the screen; the arrows are not worth a failure */ });
}

/** The mover rows the app is holding: the fetched ones, or the sample's. */
export const racingMoverRows = computed(() => racingMovers.value?.movers
  ?? ((isDemo.value || sampleMode.value) ? demoMovers() : []));

/**
 * The movers as the screens read them: `Map<matchKey, Map<selection, mover>>`.
 *
 * Filed by the race's own key, so a row on the card and a runner on the race
 * page both find their arrow with a lookup and neither matches on a name.
 */
export const raceMovers = computed(() => moversByRace(racingMoverRows.value));

/**
 * IS THIS DEVICE A RACING DEVICE AT ALL? A key, a code switched on, and none of
 * the three refusals that stop anything being bought.
 *
 * Separate from `canRefreshRacing` below because it is a different question,
 * and the 2026-09-16 change turns on the difference. "Is the card on offer on
 * the screen in front of me" has to know whether a card has landed; "does this
 * punter follow the races" does not, and a Refresh on Today must buy the card
 * for somebody who has not opened the Racing tab since breakfast.
 */
export const racingEnabled = computed(() => {
  if (isDemo.value || sampleMode.value || onBreak.value) return false;
  if (!racingKeyed.value) return false;
  return enabledRacingSports.value.length > 0;
});

/**
 * Is buying the card again on offer?
 *
 * `racingEnabled` plus a card on the screen to buy again. The button is not
 * offered rather than offered and refused, and `loading` counts because a pull
 * flips the status for a moment and a control that vanished under the thumb
 * that pulled it would be worse than one that did nothing.
 */
export const canRefreshRacing = computed(() => racingEnabled.value
  && (racingStatus.value === 'ready' || racingStatus.value === 'loading'));

/**
 * The card as the engine reads it: one Match per race, its `selections` the
 * active runners and its `prices.r4` every book paying for number 4.
 *
 * `sameDay` is OFF, and that is the one option this differs from `universe` in.
 * A racing card is not a calendar day — the board's window is "still to jump
 * today, plus tomorrow morning" — so cutting it on the device's date would
 * throw away the late races the moment the clock rolled over in Sydney.
 */
/**
 * The card the app is printing from: the fetched one, or the sample.
 *
 * THE SAMPLE DOES NOT WAIT FOR THE TAB. `loadRacing` seeds `racingSnapshot` on
 * a tab enter, which is right for a card that costs credits and wrong for one
 * that costs nothing: on /demo the racing strategies would print "no card yet"
 * until somebody happened to open Racing, and the Today feed is where a reader
 * meets them. Nothing here fetches — a sample card is six objects in memory.
 */
export const racingCard = computed(() =>
  racingSnapshot.value ?? ((isDemo.value || sampleMode.value) ? demoCard() : null));

/** Is there a racing card on the device at all? What the blank note turns on. */
export const hasRacingCard = computed(() => (racingCard.value?.races?.length ?? 0) > 0);

export const raceUniverse = computed(() => {
  const card = racingCard.value;
  const asOf = card?.fetchedAt ?? new Date(now.value).toISOString();
  // THE CODES ARE CUT HERE AND NOWHERE ELSE. Every screen, every strategy, the
  // builder and the notifier all read this one board, so a code switched off in
  // Settings goes off everywhere at once. It is cut off the card rather than off
  // the request because a CACHED card was bought before the switch was thrown —
  // filtering the request alone would leave last hour's greyhounds on the
  // screen until the card went stale.
  // THE RATINGS GO ON THE ROWS, BEFORE THE FOLD. `runners` is in `RACE_FIELDS`,
  // so a rating written onto the row's runner bundle rides into the match the
  // engine builds and `ratingDisagreement` can be measured off it. Nothing is
  // mutated: `attachRatings` rebuilds the bundle per race and answers the rows
  // unchanged where there is nothing to join, which is most days and every
  // harness card.
  const rows = attachRatings(
    fromRaces(racesForCodes(card?.races, racingCodes.value), { asOf }),
    racingFeatures.value,
  );
  return buildUniverse(rows, {
    forDate: card?.forDate ?? localDate(now.value, RACING_TZ),
    asOf,
    timeZone: RACING_TZ,
    sameDay: false,
  });
});

// --- multi, leg --------------------------------------------------------------
// The seed is the tie-break Classic fills each tier with, so a bump deals a
// different four from the same board at the same prices. 0 is the default.
export const currentSeed = ref(0);
// Every deal kept today, oldest first. This is the PAPER TRAIL's half of the
// day — `lib/proposals.js` reads it back and the morning settle scores it — and
// it is no longer a thing the screen steps through. Today reads `timeline`
// below, which is the same tickets as one running list.
export const generations = ref([]);

function loadGenerations(date) {
  try {
    const all = prefs[GENERATIONS_KEY] || {};
    return Array.isArray(all[date]) ? all[date] : [];
  } catch { return []; }
}

function saveGeneration(date, gen) {
  try {
    const all = prefs[GENERATIONS_KEY] || {};
    const list = Array.isArray(all[date]) ? all[date] : [];
    list.push(gen);
    all[date] = list;
    prefs[GENERATIONS_KEY] = all;
  } catch { /* private mode */ }
}

/**
 * Sixty days of sets, picks and refusals, and no more.
 *
 * All three halves of the paper trail are localStorage, which is small and
 * shared with everything else the app keeps. Run once at boot, and only written
 * back when a day actually went — an untouched store is not rewritten.
 */
function pruneKeptProposals(date) {
  for (const key of [GENERATIONS_KEY, PICKS_KEY, REJECTIONS_KEY]) {
    try {
      const all = prefs[key];
      if (!all || typeof all !== 'object') continue;
      const kept = pruneOld(all, KEEP_DAYS, date);
      if (Object.keys(kept).length !== Object.keys(all).length) prefs[key] = kept;
    } catch { /* private mode */ }
  }
}

/**
 * When the tickets on the LIVE pad were dealt, as opposed to when their prices
 * were read. Null until something actually deals: a board read at noon was not
 * dealt at noon, it was dealt when it landed, and a meta line claiming
 * otherwise would tell a punter their prices are fresher than they are.
 */
const padDealtAt = ref(null);

if (inBrowser) {
  pruneKeptProposals(today.value);
  generations.value = loadGenerations(today.value);
  watch(today, (d) => {
    currentSeed.value = 0;
    padDealtAt.value = null;
    generations.value = loadGenerations(d);
  });
}

/**
 * WHEN THE PAD IS DEALING AS OF. One expression, read by both the universe and
 * the runner context, because `buildUniverse` and Classic's `buildPool` are two
 * filters on one idea and a board they disagreed about would print a ticket the
 * ranking could not score.
 *
 * Not the same thing as when the prices were read — that is `pricesFetchedAt`,
 * and it still rides in `ctx.oddsFetchedAt` so a ticket's stamp and its STALE
 * mark keep meaning the fetch.
 */
const padAsOf = computed(() => dealAsOf({
  fetchedAt: (snapshotIsCurrent.value ? snapshot.value?.fetchedAt : null)
    ?? new Date(now.value).toISOString(),
  dealCutoff: dealCutoff.value,
  allowStarted: riskProfile.value.allowStarted === true,
}));

/** Today's whole board: every match still to start, with every bookmaker's price. */
export const universe = computed(() => {
  const s = snapshotIsCurrent.value ? snapshot.value : null;
  const asOf = padAsOf.value;
  const rawEvents = s?.events ?? [];
  return buildUniverse(fromOddsApiEvents(rawEvents, { asOf }), {
    forDate: s?.forDate ?? padDate.value,
    asOf,
    timeZone: deviceZone(),
    sameDay: !isDemo.value,
    rawEvents,
  });
});

/**
 * How many of today's matches are already under way — counted against the DEAL
 * CUTOFF, not the clock, so it answers the question the pad is actually asking:
 * how many matches the next set is not allowed to touch.
 *
 * Today reads it to tell the two dead ends apart when "Another set" finds
 * nothing: a board too small to deal twice, and a board whose spare matches
 * have all kicked off.
 */
export const startedOnBoard = computed(() => {
  const events = snapshotIsCurrent.value ? snapshot.value?.events ?? [] : [];
  return events.filter((e) => {
    const start = new Date(e?.commence_time ?? '').getTime();
    return Number.isFinite(start) && start <= dealCutoff.value;
  }).length;
});

/** Which sports are actually on the board today. What the models are fetched for. */
export const sportsInUniverse = computed(() => {
  const sports = new Set();
  for (const match of universe.value.matches.values()) sports.add(match.sport);
  return [...sports].sort();
});

/**
 * What fetching the prices again would cost: one credit per board with a match
 * today. That is exactly what the server pays for — `rankSports` prices the
 * boards that have matches and skips the rest — so the number the app prints
 * before spending is the number it spends.
 */
export const refreshEstimate = computed(() => sportsInUniverse.value.length);

/**
 * The models the pad can run on right now, in the shape `ctx.models` takes.
 *
 * EVERY strategy's models, not only the shown ones. A hidden strategy still
 * runs — it votes in the ranking and it goes on the paper trail — and an Elo
 * ticket missing from the ranking because nobody loaded its ratings is a
 * ranking with a hole in it. This is what the Picks screen used to do on
 * demand, every time it was opened.
 *
 * A model with no published state is LEFT OUT rather than bound to something
 * that always answers null — the engine then says "no model supplied" and
 * Today prints the honest reason, instead of claiming no leg cleared a test
 * that was never run. See lib/stores/models.js.
 */
export const padModels = computed(() =>
  bindPadModels(allStrategyDocs.value, sportsInUniverse.value, selectedProbSet.value));

// The states themselves are fetched once each per session (lib/models.js keeps
// the cache), so this only ever asks for a key it has not asked for before.
// Debounced because the board and the strategies both settle a tick apart at
// boot and neither is worth a request of its own.
if (inBrowser) {
  let modelTimer = null;
  watch([sportsInUniverse, allStrategyDocs], () => {
    if (modelTimer) clearTimeout(modelTimer);
    modelTimer = setTimeout(() => {
      modelTimer = null;
      ensureModels(modelsNeededFor(allStrategyDocs.value, sportsInUniverse.value));
    }, 250);
  }, { immediate: true });
}

// Everything a strategy needs that is not in the universe. `bookmaker` is the
// Odds API key prices are read at; `bookmakerName` is what the ticket prints.
export const runnerCtx = computed(() => {
  const s = snapshotIsCurrent.value ? snapshot.value : null;
  // The deal's cutoff, the same expression the universe is built on. The prices'
  // own time rides separately in `oddsFetchedAt`: a set dealt at 3pm on a 9am
  // board is dealt as of 3pm and PRICED at 9am, and the ticket has to say so.
  const asOf = padAsOf.value;
  return {
    forDate: s?.forDate ?? padDate.value,
    asOf,
    oddsFetchedAt: s?.fetchedAt ?? asOf,
    seed: currentSeed.value,
    stake: Number(riskProfile.value.defaultStake) || PROFILE_DEFAULTS.defaultStake,
    bankroll: riskProfile.value.bankroll ?? null,
    bookmaker: riskProfile.value.bookmaker,
    bookmakerName: bookmakerName(riskProfile.value.bookmaker),
    // WHAT EACH BOOK WILL TAKE FROM THIS PUNTER, so Kelly cannot ask for a
    // stake the shop would refuse. Empty as shipped, which means the racing
    // minimum-bet schedules and no cap at all on sport (lib/limits.js).
    bookLimits: riskProfile.value.bookLimits ?? {},
    tiersShown: tiersShown.value,
    // Undefined on purpose: the device's own zone, as the pad has always used.
    timeZone: undefined,
    sameDay: !isDemo.value,
    models: padModels.value,
    // WHAT EACH MODEL HAS BEEN SHOWN TO BE WORTH beside the market's price
    // (engine review #12), by `${modelId}:${sport}`. A model with no fit here
    // prints nothing and Today says why. `modelFits` is read so this recomputes
    // when a backtest files one; `provenFits` is what is handed over, because a
    // fit under the t threshold is not a weight anybody measured.
    modelFits: provenFits(modelFits.value),
    probSets: selectedProbSet.value?.rows ?? null,
    marketBook: marketBook.value,
  };
});

/**
 * One pass per strategy, kept whole: { strategy, tickets, candidates, notes }.
 *
 * EVERY strategy runs, hidden or not. A hidden strategy is hidden from the
 * SCREEN — it still has an opinion about today's board, that opinion still
 * belongs in the ranking the shown ones are ordered by, and the picks it earns
 * still go on the paper trail, which is what its card's evidence is counted
 * from. Hiding is where a strategy stops printing, not where it stops running.
 *
 * Today needs the strategy a ticket came from AND the reason a strategy
 * printed nothing — "no leg cleared the edge test today" is a result, not an
 * absence, and a pad that silently drops a strategy on a quiet day looks
 * broken.
 *
 * ON LEAVING THIS A PLAIN COMPUTED. Eight strategies plus `rankTickets` is
 * exactly what the Picks screen ran on every open, and it was fast enough
 * there to run on demand. Nothing here depends on `now`, so the minute clock
 * does not touch it: it re-runs when the board, the seed, the profile, the
 * models or the market book move, and those are the only things that should
 * change what a strategy says.
 */
/**
 * The same context over the RACING CARD, which is a different board with a
 * different clock.
 *
 * Three things differ and each of them is the card's own, not the pad's:
 * `forDate` and `oddsFetchedAt` come off the card (it is bought on demand and
 * is usually fresher than the morning's board, which is exactly what a racing
 * ticket's stamp has to say), `asOf` is when the DEAL is being cut, and the
 * zone is Sydney, because a meeting's day is the meeting's own.
 *
 * THE DEAL'S CLOCK, NOT THE CARD'S, and on a card that is the whole ball game.
 * `raceUniverse` is cut at the fetch, which is right for the Racing tab: it is
 * the card the punter paid for and every race on it belongs on the screen. A
 * DEAL is a different question. A card bought at two o'clock still lists the
 * 2:10, and at five to three a ticket written on the 2:10 is a ticket on a race
 * that has been run — which is how a racing ticket came to be born expired. So
 * the deal is cut the same way the pad's is (`dealAsOf`): the later of the
 * fetch and the deal cutoff, which the refill moves to now every time it runs.
 * `daysToKickoff` then drops a race behind the cut on its own, and the lead
 * time (`RACING_LEAD_MS`) is measured from the same moment.
 *
 * `models` is not passed: no racing strategy has a model to bind, and handing
 * one the football models would be an argument nothing reads.
 */
export const racingCtx = computed(() => {
  const card = racingCard.value;
  const board = raceUniverse.value;
  return {
    ...runnerCtx.value,
    forDate: board.forDate,
    asOf: dealAsOf({
      fetchedAt: board.asOf,
      dealCutoff: dealCutoff.value,
      allowStarted: riskProfile.value.allowStarted === true,
    }),
    oddsFetchedAt: card?.fetchedAt ?? board.asOf,
    timeZone: RACING_TZ,
    sameDay: false,
    models: {},
  };
});

/**
 * ONE PASS PER STRATEGY, EACH OVER THE BOARD IT IS ABOUT. The sports board for
 * everything that has ever run here; the racing card for the three that deal
 * from it. A racing strategy run over the football board would look at nothing
 * and report "nothing fits", which is a true sentence about the wrong question.
 *
 * With no card on the device the racing ones print nothing and say so, in the
 * one note of this app's that is already in the punter's voice — the card is
 * two credits and is not going to be bought by a strategy wanting to run.
 *
 * A RACING STRATEGY IS NARROWED WHERE IT RUNS, never in the catalogue. The
 * documents say all three codes because that is what the strategies ARE; which
 * of them a device deals from is this device's setting, and writing it into the
 * catalogue would edit a builtin, put a punter's switch inside a fork of it, and
 * leave the backtests reading a document that means something different on every
 * phone. `forCodes` hands the runner a copy for the length of one pass.
 */
const forCodes = (s) => {
  const sports = (s.filters?.sports ?? []).filter((x) => enabledRacingSports.value.includes(x));
  // Never empty: the last code cannot be switched off. A strategy that named
  // nothing this device carries keeps its own list rather than becoming a
  // strategy with no sports, which `isRacingStrategy` would stop calling racing.
  return sports.length ? { ...s, filters: { ...s.filters, sports } } : s;
};

export const allResults = computed(() => {
  // ONE DAY, ONE EXPOSURE BUDGET (engine review #13). Created here, once per
  // pass, and handed to every run in it — so the Kelly strategies spend the
  // same 10% of bankroll between them in the order they print, instead of each
  // of them spending it alone. Classic and the all-up stake flat and never draw
  // on it. A fresh pass is a fresh budget, which is the right answer: this
  // recomputes when the board, the seed or the profile moves, and that is a new
  // day's worth of tickets whatever the clock says.
  const budget = createBudget();
  const ctx = { ...runnerCtx.value, budget };
  const raceCtx = { ...racingCtx.value, budget };
  return allStrategyDocs.value.map((s) => {
    if (isRacingStrategy(s)) {
      if (!hasRacingCard.value) return { strategy: s, tickets: [], candidates: [], notes: [NO_RACING_CARD] };
      const run = runStrategy(forCodes(s), raceUniverse.value, raceCtx);
      return { strategy: s, ...run };
    }
    const { tickets, candidates, notes } = runStrategy(s, universe.value, ctx);
    return { strategy: s, tickets, candidates, notes };
  });
});

/**
 * Every strategy's tickets scored against the market, best value first, and
 * why a ticket could not be scored at all. See lib/engine/picks.js — one
 * yardstick, held by someone else, for all of them.
 */
export const ranking = computed(() =>
  rankTickets(allResults.value, [universe.value, raceUniverse.value], runnerCtx.value));

/** The four best of the day. What goes on the paper trail, and what Today leads with. */
export const topPicksToday = computed(() => topPicks(ranking.value.picks));

/**
 * Nothing on today's board is worth more than the market says it is worth.
 *
 * Said plainly rather than hidden behind a small number: a day where the best
 * ticket in the app is still a loss against the market is a day to sit out,
 * and a screen that printed a ranking without saying so would be selling the
 * top of a bad list.
 */
export const nothingBeatsMarket = computed(() => !(topPicksToday.value[0]?.valueVsMarket > 0));

/**
 * What Today prints: one section per shown strategy, in ranked order, each
 * ticket carrying the pick it earned. See lib/sections.js for the order.
 *
 * Still called `padResults` because it is still the pad — the strategies shown
 * on Today, run over today's board, in the order they print.
 */
export const padResults = computed(() => {
  // The complement of `padStrategies` rather than `hiddenIds` itself. The two
  // are the same list every day of the week, and differ on exactly the day the
  // fallback above exists for: a profile that hides everything would otherwise
  // leave Today printing no sections at all while `padStrategies` was saying
  // Classic. Two answers to "what is shown" is one too many.
  const shown = new Set(padStrategies.value.map((s) => s.id));
  return sectionsFor(allResults.value, ranking.value.picks, {
    hidden: allStrategyDocs.value.map((s) => s.id).filter((id) => !shown.has(id)),
    classicId: CLASSIC.id,
  });
});

// Today's proposed tickets, flattened across the sections. With nothing hidden
// that is every strategy in the app, and with no markets every Classic ticket
// is void — which is what /multi/1 shows on the empty fixture.
const proposed = computed(() => padResults.value.flatMap((r) => r.tickets));

/**
 * THE LEGS THE BAR REFUSED TODAY, live off the same pass that printed the
 * tickets — the same list `recordRejections` files below, so the count on
 * Today's foot and the row in the log cannot disagree.
 *
 * Off `allResults` rather than `padResults`: a hidden strategy still ran and
 * still refused what it refused, and hiding a strategy is where it stops
 * printing, not where it stops thinking.
 */
export const refusedToday = computed(() => rejectionsFrom(allResults.value, { date: today.value }));

// --- the picks paper trail ---------------------------------------------------
// The day's four best, kept with the prices they were ranked at. This used to
// happen when somebody opened the Picks screen, which meant a day nobody
// opened it left no trail at all and a strategy's evidence quietly depended on
// where the reader had been. The ranking is the store's now, so the trail is
// written wherever the reader is.
//
// Browser only, like the model watcher, and debounced for the same reason: the
// board and the strategies settle a tick apart at boot, and the ranking in
// between is not a reading of anything. /demo writes nothing at all, and a day
// with no markets has no ranking to keep — an empty list would only supersede
// what an earlier fetch legitimately proposed.
if (inBrowser) {
  let picksTimer = null;
  watch([topPicksToday, pricesFetchedAt], () => {
    // The sample board has markets, which is the whole point of it — and not
    // one of them is a pick anybody made. The trail is the evidence a strategy
    // is judged on, so borrowed fixtures must never reach it.
    if (isDemo.value || sampleMode.value || !hasMarkets.value) return;
    if (picksTimer) clearTimeout(picksTimer);
    picksTimer = setTimeout(() => {
      picksTimer = null;
      recordPicks(today.value, topPicksToday.value, {
        pricesFrom: pricesFetchedAt.value,
        // Stamped against the board that is still in memory: a pick kept with
        // only a provider event id on its legs is one the trail cannot settle.
        byKey: matchIndex(universe.value),
      });
      // AND WHAT THE BAR REFUSED, on the same beat and under the same three
      // refusals above: never the demo, never the borrowed board, never a day
      // with no markets. A refusal off somebody else's Saturday would be
      // evidence about a gate nobody ran.
      recordRejections(today.value, rejectionsFrom(allResults.value, {
        date: today.value,
        pricesFrom: pricesFetchedAt.value,
      }));
    }, 250);
  }, { immediate: true });
}

// --- keeping what the pad proposed -------------------------------------------
// The pad is a computed over the board, so the moment the board moves — prices
// fetched again, a pull that found a newer board, the roll to tomorrow,
// midnight — what it was proposing is gone, unless a set had been dealt or a
// ticket placed. The paper trail settles PROPOSALS, not only bets, so the pad
// is kept as a set before anything replaces it, stamped with the prices it
// printed on.
//
// `refreshPrices` and `loadBoard` are above this and call into it; the
// functions here are declarations, so hoisting makes that fine, and they could
// not be written any earlier — `proposed` is the last thing the board derives.

/**
 * The last pad that actually printed, with the day and the prices behind it.
 *
 * This mirror is what makes midnight possible at all. `today` flipping is
 * itself what makes yesterday's board no longer current, so by the time
 * anything can react, `proposed` has already recomputed to nothing. Only a pad
 * with tickets on it is ever mirrored, so that empty recompute cannot wipe it.
 */
let lastPad = null;

/**
 * The live pad, frozen and stamped, or null when it is printing nothing worth
 * keeping. A pad of void tickets is not a proposal — but a set that has any
 * real ticket keeps all of them, which is what a reader saw.
 */
function padNow() {
  // A PAD PRINTED OFF SAMPLE DATA IS NOT A PROPOSAL, and this is the one place
  // to say so: everything that keeps a pad — the mirror below, `padFor`,
  // `keepLivePad`, and the deal's own capture in `dealFromBoard` — goes through
  // here. The board is read by identity rather than by `sampleMode`, because
  // the moment that matters is the one where a key has just been pasted, the
  // fetch is under way and the sample is still what is on the paper.
  if (isDemo.value || onSampleBoard()) return null;
  const tickets = proposed.value;
  if (!tickets.some((t) => t?.legs?.length)) return null;
  const byKey = matchIndex(universe.value);
  return {
    date: today.value,
    seed: currentSeed.value,
    pricesFrom: snapshot.value?.fetchedAt ?? null,
    tickets: tickets.map((t) => ({ multi: { ...t.multi }, legs: stampLegs(t.legs, byKey) })),
  };
}

if (inBrowser) watch(proposed, () => { lastPad = padNow() ?? lastPad; }, { immediate: true });

/** The pad to keep for a day: the live one when it is still that day's, else the mirror. */
function padFor(date) {
  const live = padNow();
  if (live && (!date || live.date === date)) return live;
  return lastPad && (!date || lastPad.date === date) ? lastPad : null;
}

/**
 * Keep one set, exactly as it printed.
 *
 * The same four at the same prices are never kept twice: a refresh that found
 * the same board, or a keep that lands on the set already at the end of the
 * day, is not a second proposal.
 */
function keepSet(date, { seed, tickets, pricesFrom, dealtAt = null, reason }) {
  const kept = loadGenerations(date);
  const last = kept[kept.length - 1];
  if (last && (last.pricesFrom ?? null) === (pricesFrom ?? null) && sameDeal(last.tickets, tickets)) return false;
  saveGeneration(date, {
    seed,
    createdAt: new Date().toISOString(),
    // WHEN IT WAS DEALT, which is not when it was kept: a pad is kept at the
    // moment something replaces it, and the timeline's "dealt 2:10 pm · prices
    // from 9:11 am" is about the first of those, not the second. Null on a
    // record written before this existed, and the reader falls back to the
    // prices' own time.
    dealtAt: dealtAt ?? null,
    tickets,
    pricesFrom: pricesFrom ?? null,
    reason,
  });
  if (date === today.value) generations.value = loadGenerations(date);
  return true;
}

/**
 * Keep what the pad is proposing right now, before something replaces it.
 *
 * `reason` is what moved it: 'prices updated', 'board refreshed', 'rolled to
 * tomorrow' or 'dealt'. `date` pins WHICH day's pad is wanted — midnight is
 * the one case where the pad on screen may already be the next day's, and
 * yesterday's is the one that has to be kept.
 */
export function keepLivePad(reason, date = null) {
  if (isDemo.value || sampleMode.value) return false;
  const pad = padFor(date);
  if (!pad) return false;
  return keepSet(pad.date, {
    seed: pad.seed,
    tickets: pad.tickets,
    pricesFrom: pad.pricesFrom,
    dealtAt: padDealtAt.value,
    reason,
  });
}

/**
 * The pad is about to print a different fetch of the board: keep it first.
 * The same prices are the same pad — nothing was lost, so nothing is kept.
 */
function keepBeforeBoard(next, reason) {
  if (!next || (next.fetchedAt ?? null) === (snapshot.value?.fetchedAt ?? null)) return false;
  return keepLivePad(reason);
}

/**
 * How many seeds a deal will try before it admits the board is too small.
 *
 * ONE NUMBER, because there is one kind of deal now. It used to be two: an
 * automatic deal asked the easy question ("is this pad different from the last
 * one") and stopped at the first seed that landed anything, and a punter's tap
 * asked the hard one ("is there a multi here that is not one I am already
 * holding"). Every deal asks the hard question now, so every deal gets the
 * seeds the hard question needs. Bounded all the same: `proposed` re-runs every
 * strategy over the whole board at each seed.
 */
const VARY_TRIES = 12;

/** How many new tickets one "Deal more" tries to land before it stops. */
const DEAL_MORE_TARGET = 4;

/**
 * HOW MANY NEW TICKETS AN AUTOMATIC DEAL LANDS, and how many a day may hold.
 *
 * THE SLOT MODEL IS GONE. A board landing, a racing card landing and a refill
 * after an expiry used to fill quotas — two live tickets per Classic design,
 * one per other strategy — and then stop, which the owner read as the app
 * having nothing left to say: *"just propose another set of bets that haven't
 * been proposed before."* So they no longer count tickets per strategy at all.
 * Each one deals a set that VARIES from everything already proposed today, and
 * what holds it back is volume rather than shape:
 *
 *   `AUTO_DEAL_TARGET`  what one deal event lands. A new board, a new card, a
 *                       refill: four new tickets, then it stops. The day's
 *                       FIRST deal is the exception and prints the pad whole —
 *                       each strategy shown says its own piece (Classic's four
 *                       ways, the all-up's three) and cutting that at four
 *                       would be the morning arriving half-written.
 *   `AUTO_DEAL_DAILY`   what the whole day may add on its own. The feed is
 *                       meant to GROW through the day; it is not meant to
 *                       become a list of everything the engine can produce,
 *                       which is what the old reshuffle already was.
 *
 * Deal more is held to neither: `AUTO_DEAL_TARGET`'s twin `DEAL_MORE_TARGET` is
 * still four a tap, and the day's cap is about what the app does UNASKED.
 */
export const AUTO_DEAL_TARGET = 4;
export const AUTO_DEAL_DAILY = 24;

const placedMultis = ref([]);
const placedLegs = ref([]);

// --- bet-record --------------------------------------------------------------
export const betRecords = ref([]);

// Sample history, /demo only: demoData returns [] everywhere else, and on the
// QA `empty` fixture, so History's empty state can be photographed.
const seeded = demoData(demoHistoryRows(Date.now()));
watch(seeded, (rows) => {
  if (!rows.length) return;
  placedMultis.value = rows.filter((r) => r.entity === 'multi');
  placedLegs.value = rows.filter((r) => r.entity === 'leg');
  betRecords.value = rows.filter((r) => r.entity === 'bet-record').sort(newestFirst);
}, { immediate: true });

/**
 * Resolves once stored rows are loaded. Everything that writes awaits it.
 *
 * This is also the only place rows are migrated. A row written before the
 * strategy engine has no `v` and none of the fields the engine reads; every row
 * is brought forward here, and only the ones that actually changed are written
 * back — see lib/migrate.js. A failed write is not fatal: the rows in memory
 * are already the migrated ones, so the app runs and tries again next boot.
 */
export const storeReady = (isDemo.value ? Promise.resolve([]) : records.all().catch(() => []))
  .then(async (stored) => {
    if (isDemo.value) return;
    const { rows, changed } = migrateRows(stored);
    placedMultis.value = rows.filter((r) => r.entity === 'multi');
    placedLegs.value = rows.filter((r) => r.entity === 'leg');
    betRecords.value = rows.filter((r) => r.entity === 'bet-record').sort(newestFirst);
    myStrategies.value = rows.filter((r) => r.entity === 'strategy').sort(byName);
    seedProbSets(rows);
    if (changed.length) {
      await Promise.all(changed.map((r) => records.put({ ...r }).catch(() => undefined)));
    }
  });

const placedById = computed(() => new Map(placedMultis.value.map((m) => [m.id, m])));

/** The ids of every multi placed, for a screen that holds its own copy of a ticket. */
export const placedIds = computed(() => new Set(placedById.value.keys()));
const legsByMulti = computed(() => {
  const map = new Map();
  for (const l of placedLegs.value) {
    if (!map.has(l.multiId)) map.set(l.multiId, []);
    map.get(l.multiId).push(l);
  }
  return map;
});

/** The frozen copy of a ticket you placed, or null. */
function placedTicket(id) {
  const placed = placedById.value.get(id);
  return placed ? { multi: placed, legs: legsByMulti.value.get(id) ?? [] } : null;
}

// --- hiding a ticket ----------------------------------------------------------
// A PROPOSAL THE PUNTER DOES NOT WANT TO READ AGAIN TODAY, and that is all this
// is. Hiding takes the entry off the timeline; it does NOT take it off the
// paper trail, because the app did propose it, and a record that quietly
// dropped the tickets nobody fancied would flatter every strategy on it.
// Insights and History still judge a hidden ticket exactly as before.
//
// Keyed by `ticketKey` rather than by `multi.id`, so a re-deal of the same legs
// under a fresh id stays hidden for the rest of the day — the point of hiding a
// ticket is not seeing it again, and a deal that printed it back two minutes
// later would read as the control not working.
//
// THE DAY OWNS THE LIST. It is stored against the pad's date and read back for
// that date only, so tomorrow's feed starts clean without anything having to
// remember to clear it — the same shape `notifiedOutliers` uses.
const HIDDEN_KEY = 'hiddenTicketKeys';

/**
 * The keys hidden today. A Set, replaced rather than mutated, so computeds see
 * it, and READ AT IMPORT rather than in the `inBrowser` block below: it is a
 * handful of strings out of prefs, it cannot fail (an unreadable store answers
 * an empty set), and a feed that printed the hidden tickets for one frame after
 * every launch would be the control appearing not to have worked.
 */
const hiddenKeys = ref(loadHidden(today.value));

function loadHidden(date) {
  try {
    const stored = prefs[HIDDEN_KEY];
    return new Set(stored?.date === date && Array.isArray(stored.keys) ? stored.keys : []);
  } catch { return new Set(); }
}

/** /demo writes nothing: the hide lands in the ref and goes with the reload. */
function saveHidden(date, keys) {
  if (isDemo.value) return;
  try { prefs[HIDDEN_KEY] = { date, keys: [...keys] }; } catch { /* private mode */ }
}

function setHidden(next) {
  hiddenKeys.value = next;
  saveHidden(today.value, next);
}

/**
 * The ticket a hide is about to act on: `{ entry, key }`, or null.
 *
 * The two guards are `editable`'s and they are here for its reason — the way
 * out has to hold at the store, not only at the screens that remember to leave
 * the control off. A PLACED TICKET NEVER HIDES: there is money on it, it is in
 * History, and taking it off the feed would be the app hiding a bet from the
 * person who made it.
 */
function hideable(multiId) {
  if (sampleMode.value) throw new Error(SAMPLE_REFUSAL);
  if (onBreak.value) throw new Error(breakLine(riskProfile.value));
  const entry = dealtToday.value.find((t) => t?.multi?.id === multiId);
  if (!entry) return null;
  if (placedById.value.has(multiId)) return null;
  return { entry, key: ticketKey(entry) };
}

/**
 * Take a ticket off today's feed. Answers its key, or null where there was
 * nothing to hide.
 *
 * Works on a built ticket too, and deliberately: `deleteBuilt` hides first and
 * removes second, which is how the undo window can be offered on something
 * irreversible (see `core/undo.js`).
 */
export function hideTicket(multiId) {
  const found = hideable(multiId);
  if (!found) return null;
  if (hiddenKeys.value.has(found.key)) return found.key;
  setHidden(new Set([...hiddenKeys.value, found.key]));
  return found.key;
}

/** Put one back on the feed. Answers its key, or null. */
export function unhideTicket(multiId) {
  const entry = dealtToday.value.find((t) => t?.multi?.id === multiId);
  if (!entry) return null;
  const key = ticketKey(entry);
  if (!hiddenKeys.value.has(key)) return null;
  const next = new Set(hiddenKeys.value);
  next.delete(key);
  setHidden(next);
  return key;
}

/**
 * Delete a ticket the punter built themselves — the entry AND its row on the
 * paper trail. Answers its id, or null.
 *
 * THE TRAIL GOES TOO, and only here. Every other ticket on the feed is a
 * strategy's opinion about the day and the record of it is evidence: hiding one
 * says "not for me", not "this was never proposed". A hand-built multi is
 * nobody's opinion but the punter's own — it is not evidence about anything,
 * and a scoreboard of one person's discarded drafts is not a paper trail.
 *
 * Refuses anything the engine dealt, and anything placed. `forgetProposal`
 * takes the row out of `prefs.picks` (`keepEdited` is what put it there).
 */
export function deleteBuilt(multiId) {
  const found = hideable(multiId);
  if (!found) return null;
  if (found.entry.multi.builtByHand !== true) return null;
  dealtToday.value = dealtToday.value.filter((t) => t?.multi?.id !== multiId);
  if (hiddenKeys.value.has(found.key)) {
    const next = new Set(hiddenKeys.value);
    next.delete(found.key);
    setHidden(next);
  }
  // Not into `droppedKeys`: a deleted draft never happened, so building the
  // same multi again has to work.
  if (!isDemo.value && !sampleMode.value) forgetProposal(today.value, multiId);
  return multiId;
}

// --- the timeline -------------------------------------------------------------
// Today is ONE LIST now, not a stack of sets. Everything the app has dealt
// today lives in `dealtToday` — the live pad, every deal before it, every
// refill — keyed by `multi.id` and stamped with when it was dealt and which
// fetch of the prices it was dealt on. Nothing is ever removed from it: a
// ticket whose match has started is not gone, it is above the fold, dimmed,
// with its stamps still on it.
//
// NOTHING IN HERE FETCHES ANYTHING. Dealing more, refilling after an expiry
// and reclassifying live → expired are all computed from the board that is
// already on the device, at the prices it already carries. That is the design
// constraint, not an implementation detail: a timeline that re-priced itself as
// matches kicked off would spend a credit every few minutes with nobody having
// touched a thing.

/**
 * Every ticket dealt today, in the order it was dealt:
 * `{ multi, legs, pick, dealtAt, pricesFrom }`.
 *
 * Seeded from the day's kept deals at boot, so a reopen finds the morning's
 * proposals where it left them rather than a fresh pad pretending nothing was
 * ever printed.
 */
export const dealtToday = ref([]);

/**
 * How many tickets the app has put on today's feed WITHOUT being asked:
 * `AUTO_DEAL_DAILY`'s running total.
 *
 * Counted rather than stored. It is a volume guard on a list that is rebuilt
 * from `prefs.generations` at every boot, nothing it guards spends anything,
 * and a number that survived a restart would be buying safety the app does not
 * need with a preference key it would then have to prune.
 *
 * THE TALLY BELONGS TO THE LIST. An empty `dealtToday` is a day on which
 * nothing has been dealt automatically, whatever the counter last held — so it
 * is cleared with the list rather than only at midnight, which is also what
 * makes the day roll (`watch(today)` reloads the list) carry it.
 */
const autoDealt = ref(0);
watch(dealtToday, (list) => { if (!list.length) autoDealt.value = 0; }, { flush: 'sync' });

/** What is left of the day's automatic allowance. */
const autoRoom = () => Math.max(0, AUTO_DEAL_DAILY - autoDealt.value);

/** Count what an automatic deal landed. Nothing landed, nothing counted. */
const noteAutoDealt = (n) => { if (n > 0) autoDealt.value += n; };

/** The kept deals for a day, flattened into timeline entries. */
function dealtFromGenerations(list) {
  const out = [];
  for (const gen of list ?? []) {
    for (const t of gen?.tickets ?? []) {
      if (!t?.multi?.id || !t.legs?.length) continue;
      out.push({
        multi: t.multi,
        legs: t.legs,
        pick: t.pick ?? null,
        dealtAt: gen.dealtAt ?? gen.pricesFrom ?? gen.createdAt ?? null,
        pricesFrom: gen.pricesFrom ?? t.multi.oddsFetchedAt ?? null,
      });
    }
  }
  return out;
}

/** The bet record for a multi, so the timeline can tell placed from resulted. */
const betByMulti = computed(() => new Map(betRecords.value.map((b) => [b.multiId, b])));

/**
 * The list the timeline is computed from: the placed copy where there is one,
 * the strategy document it belongs to, and the stamps it was dealt with.
 *
 * A strategy hidden since is dropped — hiding is where a strategy stops
 * printing, and a timeline that kept printing it would contradict Settings.
 */
const timelineSource = computed(() => {
  const shown = new Map(padStrategies.value.map((s) => [s.id, s]));
  const out = [];
  for (const t of dealtToday.value) {
    const sid = t.multi.strategyId ?? CLASSIC.id;
    // A ticket the punter built is theirs, not a strategy's, so hiding a
    // strategy in Settings must not take it off the feed. Everything else falls
    // back to Classic, which is what a pre-strategies ticket is.
    if (!t.multi.builtByHand && !shown.has(sid)) continue;
    // Hidden today. It stays in `dealtToday` and stays on the paper trail —
    // this is the one list it leaves. See `hideTicket`.
    if (hiddenKeys.value.has(ticketKey(t))) continue;
    const placed = placedTicket(t.multi.id);
    out.push({
      multi: placed ? placed.multi : t.multi,
      legs: placed ? placed.legs : t.legs,
      pick: t.pick ?? null,
      // A built ticket has no strategy document, and handing it Classic's — the
      // fallback `sid` resolves to — would print somebody's own multi on the
      // feed under Classic's name. Its own frozen label is what it is called.
      strategy: t.multi.builtByHand ? null : shown.get(sid),
      dealtAt: t.dealtAt,
      pricesFrom: t.pricesFrom,
    });
  }
  return out;
});

const timelineResult = computed(() => timelineOf(timelineSource.value, {
  now: now.value,
  placedBy: betByMulti.value,
  allowStarted: riskProfile.value.allowStarted === true,
  strategyOrder: padStrategies.value.map((s) => s.id),
}));

/**
 * The day's proposals in kick-off order: `{ id, ticket, strategy, state,
 * cutoff, dealtAt, pricesFrom }`. Recomputes on the minute clock, which
 * RECLASSIFIES only — a ticket moving from live to expired is the clock
 * telling the truth, and nothing here deals as a consequence of it.
 */
export const timeline = computed(() => timelineResult.value.entries);

/** Where "Now" sits in `timeline` — the first live entry, or the foot. */
export const foldIndex = computed(() => timelineResult.value.foldIndex);

/** A slot key onto a list, once, in the order it was first seen. */
function note(list, slot) {
  if (slot && !list.includes(slot)) list.push(slot);
}

/**
 * Every strategy's id → its name, for naming a slot a deal reports on.
 *
 * Off `allStrategyDocs`, so a strategy saved on this device names itself and a
 * strategy deleted since names nothing — `slotName` falls back to "Ticket"
 * rather than printing an id into a toast.
 */
const strategyNames = computed(() => {
  const out = {};
  for (const s of allStrategyDocs.value) out[s.id] = s.name;
  return out;
});

/** A slot key as a punter reads it: a Classic tier, or a strategy's name. */
const nameOfSlot = (slot) => slotName(slot, strategyNames.value);

/**
 * What the foot of Today offers to put back: `{ id, name, legs, odds }` per
 * hidden ticket, in the order they were dealt.
 *
 * A BUILT TICKET IS NEVER IN HERE. It is deleted rather than hidden, and the
 * hide it goes through on the way (see `deleteBuilt`) is the undo window, not a
 * state anybody is meant to find under "Show hidden".
 */
export const hiddenToday = computed(() => {
  if (!hiddenKeys.value.size) return [];
  const out = [];
  for (const t of dealtToday.value) {
    if (t?.multi?.builtByHand === true) continue;
    if (!hiddenKeys.value.has(ticketKey(t))) continue;
    out.push({
      id: t.multi.id,
      name: nameOfSlot(slotOf(t.multi)),
      legs: t.legs?.length ?? 0,
      odds: t.multi.combinedOdds ?? null,
    });
  }
  return out;
});

/**
 * Put tickets on the timeline. Answers WHAT LANDED, not merely how much:
 * `{ added, slots, exhausted, offered }`, every list a set of slot keys in the
 * order the pad printed them.
 *
 * TWO RULES, AND THEY ARE THE WHOLE OF THE MODEL NOW: nothing already proposed
 * today is proposed again — not the same legs under a fresh id (`ticketKey`),
 * and not one of them with a leg off or a leg on (`variesFrom`) — and a deal
 * stops at `room`, which is the caller's volume for this event.
 *
 * THE THIRD RULE WAS A CAP AND IT IS GONE. A slot holding its quota of live
 * tickets used to take no more, which is how an automatic deal came to say
 * nothing on an afternoon with a whole board still to bet. Volume is the
 * caller's business (`AUTO_DEAL_TARGET`, `AUTO_DEAL_DAILY`, `DEAL_MORE_TARGET`)
 * and variety is this function's, and neither of them is a shape.
 *
 * `exhausted` is the first rule SAID OUT LOUD — the slots that offered a ticket
 * the feed was already holding, by content. It is the one dead end a punter can
 * do nothing about, and without it the screen can only say "nothing more",
 * which is what a broken button says too.
 */
/**
 * WHERE A TICKET'S NUMBERS ACTUALLY CAME FROM. Two boards are dealt in one
 * pass and they carry two fetch stamps: a racing ticket is written on the card,
 * a football one on the sports board, and one stamp over both would have a
 * ticket printed off a card bought five minutes ago reading "prices from 9:11
 * am". The line under the ticket states this figure out loud, so it has to be
 * the truth about the prices ON that ticket.
 */
const pricesFromFor = (ticket, fallback) => (
  racingStrategyIds.value.has(ticket?.multi?.strategyId)
    ? (racingCard.value?.fetchedAt ?? fallback ?? null)
    : (fallback ?? null));

function appendDealt(tickets, {
  at, pricesFrom, liveOnly = false, room = null,
}) {
  const allowStarted = riskProfile.value.allowStarted === true;
  // By content, not by id: the same legs dealt again under a fresh id is the
  // same ticket, and the list must not hold it twice. See `ticketKey`.
  //
  // A TICKET SOMEBODY HAS TAKEN A LEG OFF IS STILL SPOKEN FOR. It is no longer
  // on the list, so nothing else here would stop the next deal printing it
  // straight back — the dropped leg would return two minutes later under a
  // fresh id, beside the ticket it was dropped from. See `dropLeg`.
  //
  // A HIDDEN TICKET SPENDS THE SAME COIN. It is still in `dealtToday`, so it is
  // already in this set — it is named anyway, because the rule is the day's and
  // not the list's: a ticket somebody took off the feed must not be dealt back
  // to them this afternoon, however it left.
  const have = new Set([
    ...dealtToday.value.map(ticketKey), ...droppedKeys, ...hiddenKeys.value,
  ]);
  // The legs already dealt today, per slot — what the variety rule judges a
  // candidate against. Every ticket on the list, hidden ones included: the
  // punter has seen it, and offering them a near-copy of the one they hid is
  // not an answer to "propose something that has not been proposed before".
  //
  // READ ONCE, AT THE DAY AS IT STOOD BEFORE THIS PAD, and nothing added below
  // joins it. A pad is one strategy's whole answer and the pieces of that
  // answer are allowed to be versions of each other — the all-up is the SAME
  // card read at two legs, three and four, so the three-leg one is the two-leg
  // one with a leg on, by design. Judged against each other they would eat
  // themselves and the strategy would print one ticket instead of three.
  // Across deal events the accumulation still happens, because `dealtToday` has
  // grown by then: `dealFromBoard` re-enters this function at every seed.
  const legSets = new Map();
  for (const t of dealtToday.value) {
    const slot = slotOf(t.multi);
    if (!legSets.has(slot)) legSets.set(slot, []);
    legSets.get(slot).push(legSetOf(t));
  }

  const added = [];
  const filled = [];
  const exhausted = [];
  const offered = [];
  for (const t of tickets ?? []) {
    const multi = t?.multi;
    // A void tier has no legs, no kick-off and nothing to propose. It is a
    // strategy's answer about the board, and Today prints those at the foot —
    // it never OFFERED anything, so it is not a slot that ran out either.
    if (!multi?.id || !t.legs?.length) continue;
    const slot = slotOf(multi);
    note(offered, slot);
    if (have.has(ticketKey(t))) { note(exhausted, slot); continue; }
    // NOT PROPOSED BEFORE, which is the owner's whole sentence: a ticket that
    // is one already on the feed with a leg off — or with a leg on — is not
    // another set of bets. Same slot only: Classic's four ways ARE different
    // tickets from each other by design, and the Low one being the first two
    // legs of the High one is the shape of the pad rather than a repeat of it.
    // Counted as exhausted, because that is exactly what "the board has
    // nothing else to say here" is.
    if (!variesFrom(legSets.get(slot), legSetOf(t))) { note(exhausted, slot); continue; }
    // A REFILL MAY ONLY ADD SOMETHING LIVE. On a board whose last matches have
    // all started, every deal comes back already expired — it answers nothing,
    // the feed is as empty a minute later, and the timeline would grow a dead
    // ticket every sixty seconds forever.
    if (liveOnly && ticketState(t, { now: now.value, allowStarted }) !== 'live') continue;
    have.add(ticketKey(t));
    note(filled, slot);
    added.push({
      multi: { ...multi },
      legs: t.legs.map((l) => ({ ...l })),
      pick: t.pick ?? null,
      dealtAt: at ?? null,
      pricesFrom: pricesFromFor(t, pricesFrom),
    });
    // Enough. `room` is what is left of the caller's target, and a deal that
    // kept going would empty the board's variety into one tap.
    if (room !== null && added.length >= room) break;
  }
  if (added.length) dealtToday.value = [...dealtToday.value, ...added];
  return { added: added.length, slots: filled, exhausted, offered };
}


/**
 * Deal from the board already on the device: bump the seed so the fill breaks a
 * different way, keep what the pad was proposing, and put whatever is new on
 * the timeline. Answers `{ added, slots, exhausted, offered }` — the counts and
 * the slots, NAMED, ready for the screen to make a sentence of.
 *
 * The seed is a tie-break, not a shuffle, so on a small board the next one can
 * deal the identical four — QA saw seeds 1 and 2 print the same pad — and the
 * button then reads as broken. So it walks forward until the deal actually
 * changes AND puts something on the list, and says nothing was dealt rather
 * than leaving the reader to spot a set they have already read.
 *
 * `exhausted` and `offered` are the UNION over every try, because every try is
 * this board answering the same question — a tier that offered the ticket
 * already on the feed at three seeds running has run out at all three, and
 * reporting only the last of them would name it or not by accident.
 *
 * `auto` is the app dealing without being asked — a new card, a refill after an
 * expiry. It is not a different KIND of deal any more (that was the slot model,
 * and it is gone): it is the same deal counted against the day's own volume,
 * `AUTO_DEAL_DAILY`.
 */
function dealFromBoard({
  liveOnly = false, reason, target = 0, auto = false,
}) {
  // The day's room comes off the top, so an automatic deal near the cap lands
  // what is left of it rather than the whole four.
  const want = Math.min(target || Infinity, auto ? autoRoom() : Infinity);
  if (want <= 0) return { added: 0, slots: [], exhausted: [], offered: [] };
  const from = currentSeed.value;
  const current = proposed.value;
  // Frozen before the seed moves: the pad about to be replaced is the one kept.
  const keeping = padNow();
  // Event (b): a deal is cut AS OF NOW, so a match that has kicked off since
  // the prices were fetched is not on it. After the old pad is captured, so
  // what is kept is what was read; before the loop, so every candidate deal is
  // judged on the same board the winner will be dealt from.
  dealCutoff.value = now.value;
  const at = isoSeconds(now.value);
  const exhausted = [];
  const offered = [];
  const filled = [];
  let added = 0;
  const named = (list) => list.map(nameOfSlot);
  for (let n = 1; n <= VARY_TRIES; n += 1) {
    currentSeed.value = from + n;
    // `proposed` is a computed over the seed, so this is the pad it would print
    // at it — read back before anything is kept.
    if (sameDeal(current, proposed.value)) continue;
    const deal = appendDealt(proposed.value, {
      at,
      pricesFrom: pricesFetchedAt.value,
      liveOnly,
      room: Number.isFinite(want) ? want - added : null,
    });
    for (const s of deal.exhausted) note(exhausted, s);
    for (const s of deal.offered) note(offered, s);
    for (const s of deal.slots) note(filled, s);
    added += deal.added;
    // EVERY DEAL GOES ON UNTIL IT HAS ITS SET or the seeds run out. One
    // different ticket is not "another set of bets that haven't been proposed
    // before", and it was not what the refill was asked for either.
    if (added >= want) break;
  }
  if (auto) noteAutoDealt(added);
  if (!added) {
    currentSeed.value = from;
    return { added: 0, slots: [], exhausted: named(exhausted), offered: named(offered) };
  }
  if (keeping) {
    keepSet(keeping.date, {
      seed: from,
      tickets: keeping.tickets,
      pricesFrom: keeping.pricesFrom,
      dealtAt: padDealtAt.value,
      reason,
    });
  }
  padDealtAt.value = at;
  return {
    added,
    slots: named(filled),
    exhausted: named(exhausted),
    offered: named(offered),
  };
}

/**
 * Deal more from today's board. Answers `{ added, slots, exhausted, offered }`
 * — what landed and where, and which slots offered a ticket the feed was
 * already holding.
 *
 * IT USED TO ANSWER A BOOLEAN, and the screen could therefore only ever say
 * "nothing more to deal from this board" — the same eight words for a full
 * board, a spent one and a broken button. The tickets land in kick-off order,
 * which is rarely beside the button at the foot of the feed, so a deal that
 * cannot say what it did reads as a deal that did nothing. `lib/deal-copy.js`
 * turns this into the sentence.
 *
 * WHAT HOLDS IT BACK IS THE BOARD, and that is now true of every deal in the
 * app: a ticket is only dealt if it is DIFFERENT from what is already on
 * today's list — not the same legs under a new id, and not one of them with a
 * leg off or a leg on (`variesFrom`). It walks up to `VARY_TRIES` seeds for
 * `DEAL_MORE_TARGET` of them and stops at whichever comes first.
 *
 * THE DAY'S CAP IS NOT ITS CAP. `AUTO_DEAL_DAILY` is about what the app does
 * unasked; a punter tapping a button has asked, and an app that answered "you
 * have had your two dozen for today" would be arguing with the person using it.
 * Four a tap is the only number here.
 *
 * FREE, and that is the point: it re-runs the strategies over prices the app
 * already has. It replaces "Another set", which threw the previous four away;
 * this one only ever adds.
 */
export function dealMore() {
  return dealFromBoard({ reason: 'dealt', target: DEAL_MORE_TARGET });
}

// --- editing a ticket: a leg off, a leg on ------------------------------------
// A punter who likes three legs of four — or all four and a fifth the engine
// never looked at — has always had two answers here: take the ticket as
// printed, or take nothing. These are the third, and they cost nothing: the
// legs keep the prices they were dealt at, `lib/engine/reprice.js` does the
// arithmetic, and no board is asked for a new price either way.
//
// THE EDITED TICKET REPLACES THE ONE IT CAME FROM on the timeline, at the same
// `dealtAt`: it is the same proposal read again with a selection struck out or
// put on, and a feed that printed both would be the app arguing with itself.
// The original is not lost — it was proposed, it is on the paper trail with the
// prices it was proposed at, and the edit goes on the trail beside it.

/**
 * The keys of tickets a leg has been taken off or put on today. `appendDealt`
 * holds them out of every later deal; the day's own watcher clears them with
 * the list. (The name is the drop's; an added leg spends the same coin — the
 * ticket the punter edited must not be dealt back to them.)
 */
const droppedKeys = new Set();

/**
 * The one edit that can be walked back: the entry as it was, the id of the
 * ticket that replaced it, and WHICH WAY it went, because the offer reads
 * "Put the leg back" after a drop and "Take them off" after an add. One level,
 * because this is an undo, not a history of everything the punter considered.
 */
const lastEdit = ref(null);

/** What the undo would act on: `{ id, kind }`, or null. Screens read this. */
export const undoableEdit = computed(() => (lastEdit.value
  ? { id: lastEdit.value.id, kind: lastEdit.value.kind }
  : null));

/**
 * Take a leg off a ticket on today's timeline. Answers the new ticket's id, or
 * null where there was nothing to do.
 *
 * A REFUSAL THROWS, the same convention `markPlaced` holds: a drop that came
 * back as null would reach `useAction` as a success and toast a leg dropped off
 * a ticket that still has it. The sample board refuses because its tickets are
 * fixtures and the trail must not carry a proposal made on them; a break
 * refuses because the way out has to hold at the store, not only at the screens
 * that remember to hide the control.
 */
export function dropLeg(multiId, index) {
  const found = editable(multiId);
  if (!found) return null;
  const next = withoutLeg(found.entry, index, universe.value);
  if (!next) return null;
  return replaceEntry(found, next, 'drop').id;
}

/**
 * Put one or more legs on a ticket, from matches on today's board. Answers
 * `{ id, note }` — the note being the best-price sentence where the ticket's
 * own book had no price — or null where there was nothing to do.
 *
 * `picks` are `{ matchKey, side }` as the picker hands them over, and they go
 * on in ONE reprice: a punter building a combo picks three matches in a visit,
 * and three separate edits would put three tickets through the trail for one
 * act. Throws the picker's refusals (`SAME_MATCH`, `legsFull`) and the same two
 * guards `dropLeg` holds.
 */
/**
 * The match a picked key names, on whichever board carries it.
 *
 * The picker ranks the sports board and the racing card as one list, so what
 * comes back out of it can be either. Sports first, because that is the board
 * every existing leg came from and the keys carry their own sport anyway.
 */
function boardMatch(matchKey) {
  return universe.value?.matches?.get?.(matchKey)
    ?? raceUniverse.value?.matches?.get?.(matchKey)
    ?? null;
}

export function addLegs(multiId, picks) {
  const found = editable(multiId);
  if (!found) return null;
  const list = (Array.isArray(picks) ? picks : [picks]).filter(Boolean);
  if (!list.length) return null;

  const book = riskProfile.value.bookmaker;
  const notes = [];
  const legs = [];
  for (const pick of list) {
    const match = boardMatch(pick.matchKey);
    // The board moved under the sheet — a refetch, or midnight. Said rather
    // than silently dropped: the punter tapped a match that is not there now.
    if (!match) throw new Error('That match is no longer on the board.');
    const built = legForTicket(found.entry.multi, match, pick.side, {
      bookmaker: book,
      bookmakerName: bookmakerName(book),
    });
    if (!built) throw new Error('No book on the board is pricing that side.');
    if (built.note) notes.push(built.note);
    legs.push(built.leg);
  }

  const next = withLegs(found.entry, legs, universe.value);
  if (!next) return null;
  return { id: replaceEntry(found, next, 'add').id, note: notes[0] ?? '' };
}

/**
 * Undo the last edit: the entry as it was, where it was. Answers the restored
 * ticket's id, or null when the offer has gone.
 *
 * No break or sample guard, and that is not an oversight — undoing an edit puts
 * no money anywhere and writes nothing. What it does not do is take the edit
 * off the paper trail: it was proposed, it is on the record, and a record that
 * rewrote itself when somebody changed their mind would be worth nothing.
 */
export function undoEdit(id) {
  const edit = lastEdit.value;
  if (!edit || edit.id !== id) return null;
  const at = dealtToday.value.findIndex((t) => t?.multi?.id === id);
  if (at === -1) return null;
  droppedKeys.delete(ticketKey(edit.entry));
  lastEdit.value = null;
  dealtToday.value = dealtToday.value.map((t, i) => (i === at ? edit.entry : t));
  return edit.entry.multi.id;
}

/**
 * The ticket an edit is about to be made to: `{ entry, at }`, or null.
 *
 * The two guards live here so a drop and an add cannot come to hold different
 * rules about a break — and `placedById` is asked as well as `reprice.js`'s own
 * status check, because the record is where money on a ticket shows up.
 */
function editable(multiId) {
  if (sampleMode.value) throw new Error(SAMPLE_REFUSAL);
  if (onBreak.value) throw new Error(breakLine(riskProfile.value));
  const at = dealtToday.value.findIndex((t) => t?.multi?.id === multiId);
  if (at === -1) return null;
  if (placedById.value.has(multiId)) return null;
  return { entry: dealtToday.value[at], at };
}

/** The edited ticket into the timeline entry's place, and onto the trail. */
function replaceEntry({ entry, at }, next, kind) {
  const replaced = {
    ...entry,
    multi: next.multi,
    legs: next.legs,
    // The day's ranking was about the ticket the app printed. This one is the
    // punter's, and a "#1 value" badge earned by a leg that is no longer on it
    // would be the old ticket's badge on the new one's paper.
    pick: null,
  };
  droppedKeys.add(ticketKey(entry));
  lastEdit.value = { id: next.multi.id, entry, at, kind };
  dealtToday.value = dealtToday.value.map((t, i) => (i === at ? replaced : t));
  keepEdited(replaced);
  return next.multi;
}

// --- building a multi from scratch ---------------------------------------------
// The picker that adds a leg to a ticket is most of a multi builder already:
// the board, ranked by what each price is worth against the other books. Open
// it on an EMPTY ticket and it is the whole of one. What comes out is a
// proposal like any other — it lands on the timeline under its own kick-off,
// it is on the paper trail, and tomorrow morning it settles beside the tickets
// the engine printed. That is the point of building it here rather than in a
// bookmaker's app: the punter's own multi gets judged by the same record.
//
// FREE, like everything else on this page. The legs are priced off the board
// already on the device.

/** How many the punter has built today, so each one gets its own id. */
let builtToday = 0;

/**
 * The empty ticket the builder starts from: the punter's own book, their own
 * default stake, the day the pad is printing. Held as a computed so the picker
 * and the print use one description of it — two would price the same leg at two
 * different books on the day somebody changed the setting mid-build.
 */
const buildOpts = computed(() => ({
  id: `hand-multi-${padDate.value}-${builtToday + 1}`,
  stake: Number(riskProfile.value.defaultStake) || PROFILE_DEFAULTS.defaultStake,
  bookmaker: bookmakerName(riskProfile.value.bookmaker),
  bookmakerKey: riskProfile.value.bookmaker,
  forDate: padDate.value,
  oddsFetchedAt: pricesFetchedAt.value,
}));

const buildBase = computed(() => newTicket(buildOpts.value));

/**
 * Every side on today's board AND every runner on today's card, best first —
 * the builder's own list.
 *
 * Both boards, ranked together on the one test, because a punter building a
 * multi is not thinking in boards: the question is which of today's prices is
 * worth taking, and a race is one of today's prices. `rankCandidates` reads
 * them in order and the race rows carry their own keys, so a runner and a
 * fixture cannot collide and two runners from one race cannot both go on.
 *
 * A code switched off in Settings is not offered here, and nothing in this
 * function has to know that: `raceUniverse` is already cut to the codes that are
 * on, so the picker's own sport chips fall out one short.
 */
export function buildCandidates() {
  return rankCandidates(buildBase.value, [universe.value, raceUniverse.value], {
    book: riskProfile.value.bookmaker,
    allowStarted: riskProfile.value.allowStarted === true,
    now: now.value,
  });
}

/** The legs those picks would print, priced exactly as the print will price them. */
function buildLegs(picks) {
  const out = [];
  const notes = [];
  for (const pick of (Array.isArray(picks) ? picks : [])) {
    const match = boardMatch(pick?.matchKey);
    if (!match) throw new Error('That match is no longer on the board.');
    const built = legForTicket(buildBase.value.multi, match, pick.side, {
      bookmaker: riskProfile.value.bookmaker,
      bookmakerName: bookmakerName(riskProfile.value.bookmaker),
    });
    if (!built) throw new Error('No book on the board is pricing that side.');
    if (built.note) notes.push(built.note);
    out.push(built.leg);
  }
  return { legs: out, notes };
}

/** The running slip while somebody is picking: what the ticket would pay. */
export function previewBuild(picks) {
  try {
    const next = buildTicket(buildLegs(picks).legs, buildOpts.value, universe.value);
    if (!next) return null;
    return { legs: next.legs.length, odds: next.multi.combinedOdds, returns: next.multi.potentialReturn };
  } catch {
    // A refusal is not a preview. The print says it properly when it is tapped.
    return null;
  }
}

/**
 * Print the punter's own multi onto today's timeline. Answers
 * `{ id, duplicate, note }`, or null with nothing to print.
 *
 * `duplicate` is the one case that does not print: the same legs at the same
 * tier are already on the feed, and a second copy of a ticket is not a second
 * ticket. The id answered is the one already there, so the screen can take the
 * punter to it rather than telling them off.
 *
 * NO CAP AND NO SLOT. A hand-built ticket is not a strategy's opinion about the
 * day, so it does not count against one (`slotOf` gives it its own key) — and
 * the guards are `dropLeg`'s, for the same reasons.
 */
export function buildMulti(picks) {
  if (sampleMode.value) throw new Error(SAMPLE_REFUSAL);
  if (onBreak.value) throw new Error(breakLine(riskProfile.value));
  const list = (Array.isArray(picks) ? picks : []).filter(Boolean);
  if (!list.length) return null;

  const { legs, notes } = buildLegs(list);
  const next = buildTicket(legs, buildOpts.value, universe.value);
  if (!next) return null;

  const key = ticketKey(next);
  const already = dealtToday.value.find((t) => ticketKey(t) === key);
  if (already) return { id: already.multi.id, duplicate: true, note: '' };

  builtToday += 1;
  const entry = {
    multi: next.multi,
    legs: next.legs,
    pick: null,
    // Dealt NOW, whatever the prices' own stamp says — the ticket did not
    // exist a minute ago, and the feed's NEW mark is about when it landed.
    dealtAt: isoSeconds(now.value),
    pricesFrom: pricesFetchedAt.value,
  };
  dealtToday.value = [...dealtToday.value, entry];
  keepEdited(entry);
  return { id: next.multi.id, duplicate: false, note: notes[0] ?? '' };
}

/**
 * The edit onto the paper trail, so it is judged like every other proposal.
 *
 * Written through `recordPicks` — the one writer of `prefs.picks` — with the
 * day's picks still on the list beside it, because that function REPLACES the
 * day and carries anything missing forward as superseded. Passing the edit
 * alone would mark every real pick of the day superseded, which is a claim
 * about the ranking that nothing here is entitled to make.
 *
 * The rank is null: an edited ticket was never ranked against the market. The
 * strategy's name comes off the multi's own frozen label, which `recordPicks`
 * falls back to.
 */
function keepEdited(ticket) {
  if (isDemo.value || sampleMode.value || !hasMarkets.value) return;
  recordPicks(today.value, [...topPicksToday.value, { rank: null, multi: ticket.multi, legs: ticket.legs }], {
    pricesFrom: pricesFetchedAt.value,
    byKey: matchIndex(universe.value),
  });
}

/** Is there a match on the board that has not kicked off yet? */
function boardHasUpcoming() {
  const events = snapshotIsCurrent.value ? snapshot.value?.events ?? [] : [];
  return events.some((e) => {
    const at = Date.parse(e?.commence_time ?? '');
    return Number.isFinite(at) && at > now.value;
  });
}

// A refill that found nothing is not worth trying again every minute until
// something changes. The key is the board, the CARD, the length of the list and
// which groups have run empty, so a new ticket, a new board, a new card or a
// new expiry all unblock it.
//
// THE CARD IS HALF THE BOARD AND THE KEY DID NOT KNOW IT. Three of the app's
// strategies deal from the racing card and none of them from `pricesFetchedAt`,
// so racing tickets that ran dry at noon stayed blocked through every card the
// afternoon bought: the sports board's stamp had not moved, the list was the
// same length, and the same groups were empty. `refillTimeline` was then asking
// a question it had already answered, over prices it had never seen.
let refillBlocked = null;

/**
 * Something on the feed has expired — deal another set that has not been
 * proposed today, from the board already on the device, at the prices already
 * on it. Answers how many it added.
 *
 * IT IS NOT FILLING A SLOT ANY MORE. It used to ask which quotas were short and
 * deal exactly into those, which is why an afternoon could go by with a live
 * board on the phone and nothing new on the feed. `emptySlots` is now only the
 * TRIGGER — has anything the punter was holding run out — and what follows is
 * the ordinary deal: up to `AUTO_DEAL_TARGET` tickets, from anywhere on the
 * board, none of them a set already proposed today.
 *
 * The refill is stamped with the CURRENT time while the prices keep theirs, so
 * the ticket can say "dealt 2:10 pm · prices from 9:11 am" and a punter knows
 * the numbers under it are the morning's without the app having to go and ask.
 */
export function refillTimeline() {
  // EITHER BOARD WILL DO. Three strategies deal from the racing card and none
  // of them from the sports board, so a device holding a card and no fixtures
  // was refusing to refill the only tickets it had.
  if (!hasMarkets.value && !hasRacingCard.value) return 0;
  const empty = emptySlots(timeline.value);
  if (!empty.length) return 0;
  // With started matches allowed, the board never shrinks under the deal — so
  // a refill can only say something new while there is a match still to come.
  if (riskProfile.value.allowStarted === true && !boardHasUpcoming()) return 0;
  const key = `${pricesFetchedAt.value}|${racingCard.value?.fetchedAt ?? ''}|${dealtToday.value.length}|${[...empty].sort().join(',')}`;
  if (refillBlocked === key) return 0;
  // The refill wants the count and nothing else: it is the minute clock dealing
  // on its own, not a punter asking a question, so there is nobody to answer.
  const { added } = dealFromBoard({
    liveOnly: true, reason: 'refilled', target: AUTO_DEAL_TARGET, auto: true,
  });
  refillBlocked = added ? null : key;
  return added;
}

/**
 * A pad landing on the feed without anybody having asked for it: the day's
 * first board, a second board, a racing card bought at three in the afternoon.
 * Answers what `appendDealt` answers.
 *
 * TWO NUMBERS AND NEITHER OF THEM IS A SLOT. `AUTO_DEAL_TARGET` is what one
 * arrival may add; `AUTO_DEAL_DAILY` is what the whole day may. Everything else
 * — nothing proposed twice, nothing that is a ticket on the feed with a leg off
 * or on — is `appendDealt`'s, and is the same rule a punter's own tap gets.
 *
 * THE DAY'S FIRST DEAL IS THE EXCEPTION AND PRINTS THE PAD WHOLE. Each strategy
 * on the screen says its own piece — Classic's four ways, the all-up's three —
 * and a morning cut off at four tickets would be a morning arriving
 * half-written. It is still counted against the day.
 */
function autoDeal(tickets, { at, pricesFrom }) {
  const room = autoRoom();
  if (room <= 0) return { added: 0, slots: [], exhausted: [], offered: [] };
  const whole = dealtToday.value.length === 0;
  const deal = appendDealt(tickets, {
    at, pricesFrom, room: whole ? room : Math.min(AUTO_DEAL_TARGET, room),
  });
  noteAutoDealt(deal.added);
  return deal;
}

/**
 * A NEW BOARD DEALS FRESH TICKETS, and the same board never re-deals.
 *
 * Watched on the fetch stamp for exactly the reason `dealCutoff` is: a
 * `loadToday()` cache hit re-assigns the same day's board, the stamp does not
 * move, and the timeline somebody left open is the timeline they come back to.
 * Flushed synchronously so the list is right before anything reads it.
 *
 * THE SECOND BOARD OF THE DAY IS NOT THE FIRST ONE AGAIN. It deals up to four
 * tickets, none of which is a set already on the feed — the same question the
 * owner asked for: *"just propose another set of bets that haven't been
 * proposed before."*
 */
watch(pricesFetchedAt, (at, was) => {
  if (!at || at === was) return;
  // THE SEED MOVES WITH THE BOARD, and only when one REPLACES another. A
  // ticket's id carries the seed the day dealt it at (`${date}-${tier}` for
  // Classic at zero, `${date}-${strategy}-s${n}` for the rest), so a second
  // board re-running at the same seed would mint the very ids already on the
  // list and the fresh tickets would be thrown away as duplicates of the stale
  // ones. The day's FIRST board keeps seed zero, so the canonical four still
  // carry the canonical ids.
  if (was) currentSeed.value += 1;
  // A fresh board's tickets were dealt when it landed, which is what its own
  // stamp says — so the meta line stays quiet until something re-deals.
  padDealtAt.value = at;
  autoDeal(proposed.value, { at, pricesFrom: at });
}, { flush: 'sync', immediate: true });

/**
 * A CARD THAT LANDS AFTER THE BOARD DEALS ITSELF.
 *
 * The sports board is fetched at boot and the watcher above deals the whole pad
 * off it. A racing card is not: it is bought when somebody opens the Racing tab
 * or pulls to refresh, which can be hours later — and at that moment three
 * strategies that had nothing to say suddenly have tickets, with nothing to put
 * them on the feed. So the card gets the same watcher the board has.
 *
 * ONLY THE RACING TICKETS, because only they are new. `appendDealt` would drop
 * the rest as duplicates by content anyway, but it would also stamp them
 * `dealtAt` the card's time and mark them NEW on the feed — a football ticket
 * dealt this morning did not become new because a race card arrived.
 *
 * AND THEN THE REFILL, which is the half that was missing. The deal above only
 * ever ADDS, and on a card whose races are the morning's it can be deduped away
 * to nothing — so the card landed, the pass added nothing, and the feed stayed
 * as empty as it was until something else moved the refill's key.
 * `refillTimeline` is the path that knows whether anything on the feed has run
 * out and refuses anything already expired, and it is free: the same board and
 * the same card the app has already paid for.
 *
 * Nothing here fetches: it reacts to a card that has already been paid for.
 */
watch(() => racingCard.value?.fetchedAt, (at, was) => {
  if (!at || at === was) return;
  const tickets = proposed.value.filter((t) => racingStrategyIds.value.has(t?.multi?.strategyId));
  if (tickets.length) autoDeal(tickets, { at, pricesFrom: at });
  // Cleared by hand as well as by key: this is a new card, which is a new
  // answer to every question the last refill asked and got nothing for.
  refillBlocked = null;
  refillTimeline();
}, { flush: 'sync', immediate: true });

if (inBrowser) {
  dealtToday.value = dealtFromGenerations(generations.value);
  // The minute clock RECLASSIFIES (the timeline is a computed over `now`) and
  // this is the one thing it triggers: once something on the feed has run out,
  // another set is dealt off the board already here. Never a fetch, never on
  // open.
  watch(now, () => { refillTimeline(); });
  watch(today, (d) => {
    refillBlocked = null;
    // Yesterday's drops go with yesterday's list: a ticket held out of the deal
    // is held out of THAT day's deal, and the offer to put a leg back dies with
    // the ticket it was about.
    droppedKeys.clear();
    lastEdit.value = null;
    // And so do yesterday's hides: what somebody did not want to read
    // yesterday morning says nothing about today's board.
    hiddenKeys.value = loadHidden(d);
    dealtToday.value = dealtFromGenerations(loadGenerations(d));
  });
}

/** What Today prints: every ticket on the timeline, the placed copy where there is one. */
export const todayTickets = computed(() => timeline.value.map((e) => e.ticket));

export const multis = computed(() => {
  const shown = todayTickets.value.map((t) => t.multi);
  const ids = new Set(shown.map((m) => m.id));
  return [...shown, ...placedMultis.value.filter((m) => !ids.has(m.id))];
});

export const legs = computed(() => {
  const ids = new Set(todayTickets.value.map((t) => t.multi.id));
  return [...todayTickets.value.flatMap((t) => t.legs), ...placedLegs.value.filter((l) => !ids.has(l.multiId))];
});

/**
 * { multi, legs } for an id: today's ticket, or a placed one from any day.
 * A bare number is a position on today's pad — `/multi/1` is today's first
 * ticket, which is the representative route the plan gives QA.
 */
export function findTicket(id) {
  const key = String(id ?? '');
  // The timeline holds every ticket dealt today — the morning's as well as the
  // pad's — each as it was dealt, so a ticket opened off it opens at the prices
  // it was proposed at rather than at whatever the same id would print now.
  const shown = todayTickets.value.find((t) => t.multi.id === key);
  if (shown) return shown;
  const placed = placedTicket(key);
  if (placed) return placed;
  if (/^\d+$/.test(key)) return todayTickets.value[Number(key) - 1] ?? null;
  return null;
}

export const findMulti = (id) => findTicket(id)?.multi ?? null;
export const betFor = (multiId) => betRecords.value.find((b) => b.multiId === multiId) ?? null;

async function persist(rows) {
  // Nothing dealt off a borrowed board is allowed into the record. `markPlaced`
  // refuses under `sampleMode` before it reaches here, and this is the same rule
  // said at the write itself — settlement, a hand-marked leg and a corrected
  // result all pass through this one function.
  if (isDemo.value || sampleMode.value) return;
  await Promise.all(rows.map((r) => records.put({ ...r })));
}

/**
 * The stake a ticket is actually placed at, and everything it decides.
 *
 * The pad prints every ticket at the Settings default, which is a suggestion —
 * "I only put $1 on the outrageous one" is the normal case, not an edge one —
 * so the number that goes on the record is whatever was really staked, and the
 * frozen multi's potential return is re-derived from it rather than carrying
 * the pad's arithmetic forward. A ticket with no odds (a void one) keeps what
 * it had: there is nothing to multiply.
 */
/**
 * What the ticket's own book will take on this bet, or null.
 *
 * NULL ON ANYTHING SHOPPED ACROSS THE BOARD, deliberately: a best-price multi
 * is several shops and a cap on "the book" would be a cap on a book nobody
 * named. A single carries `bookmakerKey`, and that is the one bet a cap can
 * honestly be applied to.
 *
 * The CODE comes off the leg: a metro thoroughbred win market is held to a
 * different minimum-bet schedule from a country one, and a sport bet is held to
 * none at all (lib/limits.js).
 */
function capForTicket(multi, ticketLegs) {
  const key = multi?.bookmakerKey;
  if (!key) return null;
  const leg = ticketLegs?.[0];
  const sport = leg?.sport ?? String(leg?.matchKey ?? '').split(':')[0];
  const code = String(sport ?? '').startsWith('racing_')
    ? codeFor(sport, venueOfKey(leg?.matchKey))
    : 'sport';
  return bookCap(riskProfile.value.bookLimits, key, { code });
}

function stakedAt(multi, stake) {
  const amount = Number(stake);
  const next = Number.isFinite(amount) && amount > 0 ? cents(amount) : Number(multi?.stake) || 0;
  const odds = Number(multi?.combinedOdds);
  return {
    stake: next,
    potentialReturn: Number.isFinite(odds) ? cents(next * odds) : (multi?.potentialReturn ?? null),
  };
}

/**
 * Mark a proposed ticket placed: freeze the multi and its legs as they are
 * now, and open a pending bet record for it. Placing twice is a no-op.
 *
 * `stake` is what actually went on, from the field beside the button. Left
 * out, the ticket's own stake stands.
 *
 * THE ONE CHOKE POINT for a break and a limit. Every screen hides the button
 * before it comes to this — Today prints a sheet where the feed was, the ticket
 * screen prints the same line where Place bet was, and the Place alert checks
 * the stake before it gets here — but a rule the punter set against themselves
 * has to hold at the place the record is actually written, not only at the
 * places that remember to ask. It THROWS rather than answering null: a refusal
 * that came back as "no bet" would reach `useAction` as a success and toast
 * "Placed" over a bet that never happened.
 */
export async function markPlaced({ multi, legs: ticketLegs }, stake) {
  await storeReady;
  // AND THE SAME CHOKE POINT FOR THE SAMPLE BOARD. Every ticket on it is built
  // from `lib/demo.js` fixtures against today's date; a record of one would be
  // a bet on a match that is not on. It throws for the reason the break does —
  // a refusal that came back as "no bet" would toast "Placed" over nothing.
  if (sampleMode.value) throw new Error(SAMPLE_REFUSAL);
  if (multi.status !== 'proposed' || placedById.value.has(multi.id)) return betFor(multi.id);
  const staked = stakedAt(multi, stake);
  if (onBreak.value) throw new Error(breakLine(riskProfile.value));
  // AND THE BOOK'S OWN CAP, at the same choke point. A single names the shop it
  // was priced at and a racing ticket names the code it belongs to, so the cap
  // is the one that book will actually take on that kind of bet; a multi across
  // the board names no shop and is capped by nothing here. See lib/limits.js.
  const allowed = stakeAllowed({
    stake: staked.stake,
    todayStaked: todayStaked.value,
    ...limits.value,
    bookCap: capForTicket(multi, ticketLegs),
    bookmakerName: multi.bookmakerKey ? bookmakerName(multi.bookmakerKey) : '',
  });
  if (!allowed.ok) throw new Error(allowed.text);
  const placed = { ...multi, ...staked, entity: 'multi', status: 'placed' };
  const frozen = ticketLegs.map((l) => ({ ...l, entity: 'leg' }));
  const bet = {
    id: `bet-${multi.id}`,
    entity: 'bet-record',
    multiId: multi.id,
    placedAt: new Date().toISOString(),
    stake: staked.stake,
    // WHERE THE BET WENT ON. A display string, because that is what the ticket
    // printed and a record has to say what the punter was told — "Best price"
    // on a multi shopped across the board, the book's own name on a single
    // that named one. The key rides beside it where there is one, so History
    // and anything that settles from the record can match the bookmaker
    // without parsing the words back.
    bookmaker: multi.bookmaker,
    bookmakerKey: multi.bookmakerKey ?? null,
    result: 'pending',
    returned: null,
    settledAt: null,
  };
  placedMultis.value = [placed, ...placedMultis.value];
  placedLegs.value = [...frozen, ...placedLegs.value];
  betRecords.value = [bet, ...betRecords.value];
  await persist([placed, ...frozen, bet]);
  return bet;
}

/**
 * A bet the app never printed, into the same ledger as the ones it did.
 *
 * `handTicket` builds it (see lib/tickets.js for the shape and why the leg
 * carries the board's own `matchKey`) and `markPlaced` writes it — every one of
 * them, with no second write path. That is deliberate: the sample board, the
 * break and the stake limits are all enforced at `markPlaced` and nowhere else,
 * so a way into the ledger that went around it would be a way around all three.
 * It throws for the same reasons `markPlaced` does, and the sheet shows the
 * sentence beside its own button.
 *
 * The book's display NAME is resolved here rather than in `tickets.js`, which
 * imports nothing; `bookmakerName` falls back to the key, so an unknown book
 * still records as something a person can read.
 */
export async function logBet({ match, side, price, bookmakerKey = null, stake, fairProbability = null }) {
  await storeReady;
  const ticket = handTicket({
    match,
    side,
    price,
    bookmakerKey,
    bookmaker: bookmakerKey ? bookmakerName(bookmakerKey) : null,
    stake,
    fairProbability,
  });
  return markPlaced(ticket, stake);
}

/**
 * Settle a bet by hand. `returned` defaults from the result — the ticket's
 * potential return for won, 0 for lost, the stake back for void — and can be
 * overridden with what the bookmaker actually paid.
 */
export async function recordResult(betId, result, returned) {
  await storeReady;
  const bet = betRecords.value.find((b) => b.id === betId);
  if (!bet) return null;
  const multi = placedById.value.get(bet.multiId);
  const fallback = { won: multi?.potentialReturn ?? null, lost: 0, void: bet.stake, pending: null }[result];
  const next = {
    ...bet,
    result,
    returned: result === 'pending' ? null : (Number.isFinite(returned) ? cents(returned) : fallback),
    settledAt: result === 'pending' ? null : new Date().toISOString(),
  };
  betRecords.value = betRecords.value.map((b) => (b.id === betId ? next : b));
  await persist([next]);
  return next;
}

/**
 * Mark one leg of a placed bet by hand: 'won', 'lost', 'void', or null to take
 * the mark off again.
 *
 * `resultSource: 'manual'` is the whole point of writing it this way.
 * `resolveLeg` in lib/stores/settlement.js hands a manual leg straight back
 * untouched, so auto-settlement can never overwrite a call somebody made
 * themselves — and clearing a mark (null) drops the source with it, which
 * hands the leg back to auto-settlement rather than freezing it as pending
 * forever.
 *
 * Returns the multi's legs AS THEY NOW STAND, so the caller can derive the
 * bet's own outcome from them without re-reading the store; null if the bet or
 * the leg is gone.
 */
export async function recordLegResult(betId, legIndex, result) {
  await storeReady;
  const bet = betRecords.value.find((b) => b.id === betId);
  if (!bet) return null;
  const leg = (legsByMulti.value.get(bet.multiId) ?? [])[legIndex];
  if (!leg) return null;
  const next = {
    ...leg,
    result: result ?? null,
    resultSource: result ? 'manual' : null,
    settledAt: result ? new Date().toISOString() : null,
  };
  // By identity, not by id: `legsByMulti` holds these very objects, and a row
  // that somehow reached the pad without an id would otherwise match every
  // other row that has none.
  placedLegs.value = placedLegs.value.map((l) => (l === leg ? next : l));
  await persist([next]);
  return legsByMulti.value.get(bet.multiId) ?? [];
}

/**
 * Change what was actually staked on a placed ticket, and move everything that
 * follows from it: the frozen multi's potential return, and — on a bet that
 * has already settled — what it returned.
 *
 * A settled bet's return is DERIVED here rather than scaled, so the figure on
 * the record is always the one these legs at this stake produce: nothing for a
 * loser, the stake back for a void, and `returnedFor` for a winner, which is
 * the only thing that knows a void leg drops out of the product. A payout that
 * was typed in by hand because the bookie paid something else is recomputed
 * with the rest — record it again if it was not the derived one.
 *
 * Answers `{ bet, multi }`, or null if the amount is not a positive number or
 * the bet is gone.
 */
export async function updateStake(betId, stake) {
  await storeReady;
  const amount = Number(stake);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  const bet = betRecords.value.find((b) => b.id === betId);
  if (!bet) return null;

  const multi = placedById.value.get(bet.multiId) ?? null;
  const legsFor = legsByMulti.value.get(bet.multiId) ?? [];
  const staked = stakedAt(multi ?? { stake: bet.stake }, amount);

  // Late import, and for the same reason settlement.js's own paper-trail
  // import is late: that module is built ON this file, so a static import back
  // would close a cycle through it. What a stake returns is ITS opinion and
  // there must not be a second copy of it here.
  const { returnedFor, betOutcome } = await import('./lib/stores/settlement.js');

  let returned = null;
  if (bet.result === 'lost') returned = 0;
  else if (bet.result === 'void') returned = staked.stake;
  else if (bet.result !== 'pending') {
    // A winner's legs know which of them were voided. Legs that were never
    // marked say nothing at all, and then the whole ticket's odds are the
    // honest answer — the same number its potential return is built from.
    returned = betOutcome(legsFor) === 'won' ? returnedFor(staked.stake, legsFor) : staked.potentialReturn;
  }

  const nextMulti = multi ? { ...multi, ...staked } : null;
  const nextBet = {
    ...bet,
    stake: staked.stake,
    returned,
    ...(bet.pnl === undefined ? {} : { pnl: bet.result === 'pending' ? null : cents((Number(returned) || 0) - staked.stake) }),
  };

  if (nextMulti) placedMultis.value = placedMultis.value.map((m) => (m.id === nextMulti.id ? nextMulti : m));
  betRecords.value = betRecords.value.map((b) => (b.id === betId ? nextBet : b));
  await persist([nextMulti, nextBet].filter(Boolean));
  return { bet: nextBet, multi: nextMulti };
}

/** Take a placed ticket off the record entirely. Returns what restoreBet needs to put it back. */
export async function removeBet(betId) {
  await storeReady;
  const bet = betRecords.value.find((b) => b.id === betId);
  if (!bet) return null;
  const multi = placedById.value.get(bet.multiId) ?? null;
  const frozen = legsByMulti.value.get(bet.multiId) ?? [];
  betRecords.value = betRecords.value.filter((b) => b.id !== betId);
  placedMultis.value = placedMultis.value.filter((m) => m.id !== bet.multiId);
  placedLegs.value = placedLegs.value.filter((l) => l.multiId !== bet.multiId);
  if (!isDemo.value) await Promise.all([bet, multi, ...frozen].filter(Boolean).map((r) => records.delete(r.id)));
  return { bet, multi, legs: frozen };
}

export async function restoreBet({ bet, multi, legs: frozen }) {
  if (multi) placedMultis.value = [multi, ...placedMultis.value];
  placedLegs.value = [...frozen, ...placedLegs.value];
  betRecords.value = [bet, ...betRecords.value].sort(newestFirst);
  await persist([bet, multi, ...frozen].filter(Boolean));
}

/** Settle-up totals over settled bets only; pending ones are counted, not summed. */
export const settleTotals = computed(() => {
  let staked = 0;
  let returned = 0;
  let pending = 0;
  for (const b of betRecords.value) {
    if (b.result === 'pending') { pending += 1; continue; }
    staked += Number(b.stake) || 0;
    returned += Number(b.returned) || 0;
  }
  return { staked: cents(staked), returned: cents(returned), net: cents(returned - staked), pending };
});

/**
 * The bets still open: how many, what is riding on them, and the most they
 * could pay. Deliberately apart from `settleTotals` — a potential return is
 * not money, and the two must never be added together.
 *
 * `potential` comes from the frozen multi, so it follows a stake that was
 * corrected after the fact. A bet whose ticket has gone contributes its stake
 * and nothing to the potential: an unknown is not a zero, but it is the only
 * number that cannot be overstated.
 *
 * `unresulted` is a SUBSET of `count`, not a fourth pile: every one of these is
 * still an open bet with money on it, and the money is still riding whether or
 * not a score ever came back. It is counted here rather than in the page
 * because `count` is here, and two places deciding what "still to settle"
 * means is how a summary line comes to disagree with the rows beneath it.
 * `lib/results-due.js` owns the rule; this only asks.
 */
export const openBets = computed(() => {
  const now = Date.now();
  let count = 0;
  let unresulted = 0;
  let staked = 0;
  let potential = 0;
  for (const b of betRecords.value) {
    if (b.result !== 'pending') continue;
    count += 1;
    if (legsOverdue(legsByMulti.value.get(b.multiId) ?? [], now)) unresulted += 1;
    staked += Number(b.stake) || 0;
    potential += Number(placedById.value.get(b.multiId)?.potentialReturn) || 0;
  }
  return { count, unresulted, staked: cents(staked), potential: cents(potential) };
});
